import {reportServices} from "../../service/reportService";
import {alertActions} from "./alertActions";
import {groupBy3Constants} from "../constants/groupBy3Constants";
import {
    allowToGenerateHash,
    generateMD5HashHex,
    GetSelectedGroupByPlaceholderFromTrafficSource,
    GetSelectedValue
} from "../../components/reporting-table/ReactDataGridUtils";
import {HasOnlyNumbersAndLetters, IsNumber} from "../../helpers/common";
import {integrationService} from "../../service/integrationService";
import ExportCSV from "../../components/export/ExportCSV";
import {v4 as uuidv4} from "uuid";
import {groupBy1Constants} from "../constants/groupBy1Constants";

export const LEVEL_1 = "DETAIL1";
export const LEVEL_2 = "DETAIL2";
export const LEVEL_3 = "DETAIL3"

export const groupBy3Action = {
    getDetailsReport,
    updateOrderBy,
    updateSortBy,
    updateSortColumns,
    rerender,
    setSelectedRows,
    updateGroupBy,
    updateStatus,
    updateBid,
    updateBudget,
    reset
};

function getDetailsReport(params, infinityScroll = false) {
    return (dispatch, getState) => {

        if (params?.isTreeView) {
            dispatch({type: groupBy1Constants.GROUP_BY_1_LOADING_SPINNER});
        } else {
            dispatch({type: groupBy3Constants.GROUP_BY_3_LOADING_SPINNER});
        }
        dispatch(alertActions.error(""));

        let groupBy1Data = getState().groupBy1?.data;
        let integration = getState().groupBy1.integration;
        let trafficSource = getState().groupBy1.trafficSource;
        let column = getState().column.data;
        let groupBy1 = getState().groupBy1.groupBy?.groupBy;
        let groupBy2 = getState().groupBy2.groupBy?.groupBy;
        let groupBy3 = getState().groupBy3.groupBy?.groupBy;
        let groupBy1Placeholder = GetSelectedGroupByPlaceholderFromTrafficSource(groupBy1, trafficSource);
        let groupBy2Placeholder = GetSelectedGroupByPlaceholderFromTrafficSource(groupBy2, trafficSource);
        let groupBy3Placeholder = GetSelectedGroupByPlaceholderFromTrafficSource(groupBy3, trafficSource);

        reportServices.getDetailsReport(params)
            .then(async (result) => {

                // CSV export
                if (params?.export === "csv") {
                    ExportCSV({fileName: "details_report_" + params?.from + "_to_" + params?.to, data: result});
                    dispatch({type: groupBy3Constants.GROUP_BY_3_STOP_LOADING_SPINNER});
                    return false;
                }

                // Add hashed custom1-10 values if column hash is not hidden.
                if (allowToGenerateHash(groupBy3, column?.detailsReport)) {
                    for (const item of result?.records) {
                        item.hash = generateMD5HashHex(item?.name);
                    }
                }

                // Add UUID
                for (const item of result?.records) {

                    item.uuid = uuidv4(); // Add UUID for rows
                    item.type = LEVEL_3; // Add data level

                    // Add TreeView additional params.
                    if (params?.isTreeView && params?.parentRowUUID !== undefined) {
                        item.isTreeView = !!params?.isTreeView;
                        item.parentRowUUID = params?.parentRowUUID;
                        item.expanded = false;
                    }
                }

                // Add loadMore button logic for TreeView
                if (params?.isTreeView && params?.parentRowUUID !== undefined) {
                    let loadMore = result?.page < Math.ceil(result?.total?.totalRecords / result?.recordsPerPage);
                    // Push one extra row
                    if (loadMore) {
                        result.records.push({
                            uuid: uuidv4(),
                            type: LEVEL_2,
                            parentRowUUID: params?.parentRowUUID,
                            loadMore: loadMore,
                            page: result?.page,
                            isTreeView: !!params?.isTreeView,
                            name: ""
                        });
                    }
                }

                // Add Pushground integration check.
                if (integration?.networkType === "pushground") {

                    let pushgroundCampaignId = GetSelectedPlaceholderNameValue({
                        getState, groupBy1Placeholder, groupBy2Placeholder,
                        treeViewData: groupBy1Data,
                        detail1ParentUUID: params?.level1RowUUID,
                        detail2ParentUUID: params?.parentRowUUID,
                        isTreeView: params?.isTreeView,
                        placeholder: "{campaign_id}",
                    });

                    let creativities = [];
                    if (groupBy3Placeholder === "{creativity_id}" && IsNumber(pushgroundCampaignId)) {
                        creativities = await integrationService.getPushgroundCampaignCreativities(integration?.id, pushgroundCampaignId);
                        if (creativities === null) {
                            creativities = [];
                        }
                    }

                    let suppliesAndSites = [];
                    let campaign = null;
                    if ((groupBy3Placeholder === "{supply_id}" || groupBy3Placeholder === "{site_id}") && IsNumber(pushgroundCampaignId)) {
                        campaign = await integrationService.findPushgroundCampaign(integration?.id, pushgroundCampaignId);
                        suppliesAndSites = await integrationService.getPushgroundCampaignSuppliesAndSitesData(integration?.id, pushgroundCampaignId);
                        if (suppliesAndSites?.blacklistedSupplies === null) {
                            suppliesAndSites.blacklistedSupplies = [];
                        }
                        if (suppliesAndSites?.blacklistedSites === null) {
                            suppliesAndSites.blacklistedSites = [];
                        }
                        if (suppliesAndSites?.supplyBids === null) {
                            suppliesAndSites.supplyBids = [];
                        }
                    }

                    for (const item of result?.records) {
                        let pushgroundId = item?.name;
                        if (pushgroundId !== "") {
                            // find Pushground campaign if group by placeholder is {campaign_id}
                            if (groupBy3Placeholder === "{campaign_id}" && IsNumber(pushgroundId)) {
                                let pResult = await integrationService.findPushgroundCampaign(integration?.id, pushgroundId);
                                if (pResult?.id > 0) {
                                    item.status = pResult?.active;
                                    item.bid = pResult?.bid;
                                }
                            }
                            // find Pushground campaign creativities if group by placeholder is {creativity_id}
                            if (groupBy3Placeholder === "{creativity_id}" && IsNumber(pushgroundId)) {
                                for (const c of creativities) {
                                    if (parseInt(c?.id) === parseInt(item?.name)) {
                                        item.status = c?.active;
                                    }
                                }
                            }
                            // find Pushground campaign supplies and sites blacklist if group by placeholder is {supply_id}
                            if (groupBy3Placeholder === "{supply_id}" && IsNumber(pushgroundId)) {
                                // Add default values
                                if (IsNumber(item?.name)) {
                                    item.status = true;
                                    if (campaign?.id > 0) {
                                        item.bid = campaign?.bid;
                                    }
                                }
                                // Add statuses
                                for (const supply of suppliesAndSites?.blacklistedSupplies) {
                                    if (parseInt(supply?.id) === parseInt(item?.name)) {
                                        item.status = supply?.active;
                                    }
                                }
                                // Add bids
                                for (const b of suppliesAndSites?.supplyBids) {
                                    if (parseInt(b?.id) === parseInt(item?.name)) {
                                        item.bid = b?.bid;
                                    }
                                }
                            }
                            // find Pushground campaign supplies and sites blacklist if group by placeholder is {site_id}
                            if (groupBy3Placeholder === "{site_id}") {
                                // Add default values
                                if (pushgroundId !== "" && pushgroundId !== "{site_id}") {
                                    item.status = true;
                                }
                                // Add statuses
                                for (const site of suppliesAndSites?.blacklistedSites) {
                                    if (site?.id === item?.name) {
                                        item.status = site?.active;
                                    }
                                }
                            }
                        }
                    }
                }

                // Add PropellerAds check
                if (integration?.networkType === "propeller-ads") {

                    let propellerAdsCampaignId = GetSelectedPlaceholderNameValue({
                        getState, groupBy1Placeholder, groupBy2Placeholder,
                        treeViewData: groupBy1Data,
                        detail1ParentUUID: params?.level1RowUUID,
                        detail2ParentUUID: params?.parentRowUUID,
                        isTreeView: params?.isTreeView,
                        placeholder: "{campaignid}",
                    });

                    // find PropellerAds campaign if group by 3 placeholder is {campaignid}
                    if (groupBy3Placeholder === "{campaignid}") {
                        let campaignIds = "";
                        for (const item of result?.records) {
                            if (item?.name?.match(/^\d+$/) !== null) {
                                if (campaignIds === "") {
                                    campaignIds += item?.name;
                                } else {
                                    campaignIds += "," + item?.name;
                                }
                            }
                        }

                        // find PropellerAds campaigns if group by placeholder is {campaignid}
                        if (campaignIds !== "") {
                            let pResult = await integrationService.findPropellerAdsCampaigns({
                                integrationId: integration?.id, campaignIds: campaignIds
                            });

                            for (const item of result?.records) {
                                for (const pCampaign of pResult) {
                                    if (parseInt(pCampaign?.id) === parseInt(item?.name)) {
                                        item.status = pCampaign?.active;
                                        item.bid = pCampaign?.bid;
                                        item.rateModel = pCampaign?.rateModel;
                                    }
                                }
                            }
                        }
                    }

                    // find PropellerAds zone if group by 3 placeholder is {zoneid}
                    if (groupBy3Placeholder === "{zoneid}" && propellerAdsCampaignId !== null) {
                        let zones = await integrationService.findPropellerAdsZoneRatesAndStatus({
                            integrationId: integration?.id,
                            campaignId: propellerAdsCampaignId
                        });
                        for (const item of result?.records) {
                            let propellerAdsId = item?.name;
                            // Add default values
                            if (propellerAdsId !== "" && propellerAdsId !== "{zoneid}") {
                                item.status = true;
                            }
                            // Set status false if includes
                            if (zones?.excludedZoneIDs.includes(parseInt(item?.name))) {
                                item.status = false;
                            }
                            // Add bid value
                            item.bid = zones?.campaignBid;
                            if (zones?.zoneRates !== null) {
                                for (const rate of zones?.zoneRates) {
                                    if (rate?.zoneId === propellerAdsId) {
                                        item.bid = rate?.bid;
                                    }
                                }
                            }
                        }
                    }

                    // find PropellerAds banner if group by 2 placeholder is {bannerid}
                    if (propellerAdsCampaignId !== null && groupBy3Placeholder === "{bannerid}") {
                        let banners = await integrationService.getPropellerAdsBanners({
                            integrationId: integration?.id,
                            campaignId: propellerAdsCampaignId
                        });
                        for (const item of result?.records) {
                            for (const banner of banners?.banners) {
                                if (parseInt(item?.name) === parseInt(banner?.id)) {
                                    item.status = banner.active;
                                    break;
                                }
                            }
                        }
                    }
                }

                // Add ExoClick check
                if (integration?.networkType === "exoclick") {

                    let campaignId = GetSelectedPlaceholderNameValue({
                        getState, groupBy1Placeholder, groupBy2Placeholder,
                        treeViewData: groupBy1Data,
                        detail1ParentUUID: params?.level1RowUUID,
                        detail2ParentUUID: params?.parentRowUUID,
                        isTreeView: params?.isTreeView,
                        placeholder: "{campaign_id}",
                    });

                    // find ExoClick campaign if group by 3 placeholder is {campaign_id}
                    if (groupBy3Placeholder === "{campaign_id}") {
                        let campaignIds = "";
                        for (const item of result?.records) {
                            if (item?.name?.match(/^\d+$/) !== null) {
                                if (campaignIds === "") {
                                    campaignIds += item?.name;
                                } else {
                                    campaignIds += "," + item?.name;
                                }
                            }
                        }

                        // find ExoClick campaigns if group by placeholder is {campaign_id}
                        if (campaignIds !== "") {
                            let pResult = await integrationService.findExoClickCampaigns({
                                integrationId: integration?.id, campaignIds: campaignIds
                            });

                            for (const item of result?.records) {
                                for (const pCampaign of pResult) {
                                    if (parseInt(pCampaign?.id) === parseInt(item?.name)) {
                                        item.status = pCampaign?.active;
                                        item.bid = pCampaign?.bid;
                                        item.rateModel = pCampaign?.rateModel;
                                    }
                                }
                            }
                        }
                    }

                    // find ExoClick zone if group by 3 placeholder is {zone_id}
                    if (groupBy3Placeholder === "{zone_id}" && campaignId !== null) {
                        let zones = await integrationService.findExoClickZoneRatesAndStatus({
                            integrationId: integration?.id,
                            campaignId: campaignId
                        });
                        for (const item of result?.records) {
                            let exoClickId = item?.name;
                            // Add default values
                            if (exoClickId !== "" && exoClickId !== "{zone_id}") {
                                item.status = true;
                            }
                            // Set status false if includes
                            if (zones?.zoneIDs.includes(parseInt(item?.name))) {
                                item.status = false;
                            }
                            // Add default status false for targeted traffic
                            if (exoClickId !== "" && exoClickId !== "{zone_id}" && zones?.targetingType === "target") {
                                item.status = false;
                                // Set status false if includes
                                if (zones?.zoneIDs.includes(parseInt(item?.name))) {
                                    item.status = true;
                                }
                            }
                            // Add bid value
                            item.bid = zones?.campaignBid;
                            if (zones?.zoneRates !== null) {
                                for (const rate of zones?.zoneRates) {
                                    if (rate?.zoneId === exoClickId && rate?.bid !== null) {
                                        item.bid = rate?.bid;
                                    }
                                }
                            }
                        }
                    }

                    // find ExoClick banner if group by 3 placeholder is {variation_id}
                    if (groupBy3Placeholder === "{variation_id}" && campaignId !== null) {
                        let banners = await integrationService.getExoClickBanners({
                            integrationId: integration?.id,
                            campaignId: campaignId
                        });
                        for (const item of result?.records) {
                            for (const banner of banners?.banners) {
                                if (parseInt(item?.name) === parseInt(banner?.id)) {
                                    item.status = banner.active;
                                    break;
                                }
                            }
                        }
                    }

                    // find ExoClick sites if group by 2 placeholder is {site_id}
                    if (groupBy3Placeholder === "{site_id}" && campaignId !== null) {
                        let sites = await integrationService.getExoClickSiteStatuses({
                            integrationId: integration?.id,
                            campaignId: campaignId
                        });
                        for (const item of result?.records) {
                            if (sites?.targetingType === "block") {
                                if (item?.name !== "{zone_id}" && IsNumber(item?.name)) {
                                    item.status = true;
                                }
                                for (const site of sites?.siteIDs) {
                                    if (parseInt(item?.name) === parseInt(site)) {
                                        item.status = false;
                                        break;
                                    }
                                }
                            }
                            if (sites?.targetingType === "target") {
                                if (item?.name !== "{zone_id}" && IsNumber(item?.name)) {
                                    item.status = false;
                                }
                                for (const site of sites?.siteIDs) {
                                    if (parseInt(item?.name) === parseInt(site)) {
                                        item.status = true;
                                        break;
                                    }
                                }
                            }
                            if (sites?.targetingType === "all") {
                                if (item?.name !== "{zone_id}" && IsNumber(item?.name)) {
                                    item.status = true;
                                }
                            }
                        }
                    }
                }

                // Add Mondiad check
                if (integration?.networkType === "mondiad") {

                    let campaignId = GetSelectedPlaceholderNameValue({
                        getState, groupBy1Placeholder, groupBy2Placeholder,
                        treeViewData: groupBy1Data,
                        detail1ParentUUID: params?.level1RowUUID,
                        detail2ParentUUID: params?.parentRowUUID,
                        isTreeView: params?.isTreeView,
                        placeholder: "[campaignid]",
                    });

                    // find Mondiad campaign if group by 3 placeholder is [campaignid]
                    if (groupBy3Placeholder === "[campaignid]") {
                        let ids = "";
                        for (const item of result?.records) {
                            if (item?.name?.match(/^\d+$/) !== null) {
                                if (ids === "") {
                                    ids += item?.name;
                                } else {
                                    ids += "," + item?.name;
                                }
                            }
                        }

                        // find Mondiad campaigns if group by placeholder 3 is [campaignid]
                        if (ids !== "") {
                            try {
                                let pResult = await integrationService.findMondiadCampaigns({
                                    integrationId: integration?.id, campaignIds: ids
                                });

                                for (const item of result?.records) {
                                    for (const pCampaign of pResult) {
                                        if (parseInt(pCampaign?.id) === parseInt(item?.name)) {
                                            item.status = pCampaign?.active;
                                            item.bid = pCampaign?.bid;
                                            item.rateModel = pCampaign?.rateModel;
                                        }
                                    }
                                }
                            } catch (e) {
                                dispatch(alertActions.error(e?.response?.data?.error));
                            }
                        }
                    }

                    // find Mondiad zone if group by 3 placeholder is [zoneid]
                    if (groupBy3Placeholder === "[zoneid]" && campaignId !== null) {
                        try {
                            let zones = await integrationService.findMondiadZoneRatesAndStatus({
                                integrationId: integration?.id,
                                campaignId: campaignId
                            });
                            for (const item of result?.records) {
                                let mondiadId = item?.name;
                                // Add default values
                                if (mondiadId !== "" && mondiadId !== "[zoneid]") {
                                    item.status = true;
                                }
                                // Set status false if includes
                                if (zones?.zoneIDs.includes(parseInt(item?.name))) {
                                    item.status = false;
                                }
                                // Add default status false for whitelisted traffic
                                if (mondiadId !== "" && mondiadId !== "[zoneid]" && zones?.targetingType === "whitelist") {
                                    item.status = false;
                                    // Set status false if includes
                                    if (zones?.zoneIDs.includes(parseInt(item?.name))) {
                                        item.status = true;
                                    }
                                }
                                // Add bid value
                                item.bid = zones?.campaignBid;
                                item.rateModel = zones?.rateModel;
                                for (const rate of zones?.zoneRates) {
                                    if (parseInt(rate?.zoneId) === parseInt(mondiadId)) {
                                        item.bid = rate?.bid;
                                        break;
                                    }
                                }
                            }
                        } catch (e) {
                            dispatch(alertActions.error(e?.response?.data?.error));
                        }
                    }

                    // find Mondiad creative if group by 3 placeholder is [creativeid]
                    if (groupBy3Placeholder === "[creativeid]") {
                        let ids = "";
                        for (const item of result?.records) {
                            if (item?.name?.match(/^\d+$/) !== null) {
                                if (ids === "") {
                                    ids += item?.name;
                                } else {
                                    ids += "," + item?.name;
                                }
                            }
                        }

                        try {
                            let banners = await integrationService.findMondiadCreatives({
                                integrationId: integration?.id,
                                ids: ids
                            });
                            for (const item of result?.records) {
                                for (const creative of banners?.creatives) {
                                    if (parseInt(item?.name) === parseInt(creative?.id)) {
                                        item.status = creative.active;
                                        break;
                                    }
                                }
                            }
                        } catch (e) {
                            dispatch(alertActions.error(e?.response?.data?.error));
                        }
                    }

                    // find Mondiad sites if group by 3 placeholder is [subid]
                    if (groupBy3Placeholder === "[subid]" && campaignId !== null) {
                        try {
                            let sites = await integrationService.findMondiadSiteRatesAndStatuses({
                                integrationId: integration?.id,
                                campaignId: campaignId
                            });
                            for (const item of result?.records) {
                                if (sites?.targetingType === "blacklist") {
                                    if (item?.name !== "[subid]" && HasOnlyNumbersAndLetters(item?.name)) {
                                        item.status = true;
                                    }
                                    for (const site of sites?.siteIDs) {
                                        if (item?.name === site) {
                                            item.status = false;
                                            break;
                                        }
                                    }
                                }
                                if (sites?.targetingType === "whitelist") {
                                    if (item?.name !== "[subid]" && HasOnlyNumbersAndLetters(item?.name)) {
                                        item.status = false;
                                    }
                                    for (const site of sites?.siteIDs) {
                                        if (item?.name === site) {
                                            item.status = true;
                                            break;
                                        }
                                    }
                                }

                                // Add bid value
                                item.bid = sites?.campaignBid;
                                item.rateModel = sites?.rateModel;
                                for (const rate of sites?.siteRates) {
                                    if (rate?.siteId === item?.name) {
                                        item.bid = rate?.bid;
                                        break;
                                    }
                                }
                            }
                        } catch (e) {
                            dispatch(alertActions.error(e?.response?.data?.error));
                        }
                    }
                }

                // Add Google Ads check
                if (integration?.networkType === "google") {

                    // find Google Ads campaigns if group by 3 placeholder is {campaignid}
                    if (groupBy3Placeholder === "{campaignid}") {
                        let ids = "";
                        for (const item of result?.records) {
                            if (item?.name?.match(/^\d+$/) !== null) {
                                if (ids === "") {
                                    ids += item?.name;
                                } else {
                                    ids += "," + item?.name;
                                }
                            }
                        }

                        // find Google Ads campaigns if group by placeholder 3 is {campaignid}
                        if (ids !== "") {
                            try {
                                let iResult = await integrationService.findGoogleAdsCampaigns({
                                    integrationId: integration?.id, campaignIds: ids
                                });

                                for (const item of result?.records) {
                                    for (const i of iResult) {
                                        if (parseInt(i?.id) === parseInt(item?.name)) {
                                            item.trafficSourceName = i?.name;
                                            if (i?.status === "ENABLED" || i?.status === "PAUSED") {
                                                item.status = i?.active;
                                            }
                                        }
                                    }
                                }
                            } catch (e) {
                                dispatch(alertActions.error(e?.response?.data?.error));
                            }
                        }
                    }

                    if (groupBy3Placeholder === "{adgroupid}") {
                        let ids = "";
                        for (const item of result?.records) {
                            if (item?.name?.match(/^\d+$/) !== null) {
                                if (ids === "") {
                                    ids += item?.name;
                                } else {
                                    ids += "," + item?.name;
                                }
                            }
                        }

                        // find Google Ads ad groups if group by placeholder 3 is {adgroupid}
                        if (ids !== "") {
                            try {
                                let iResult = await integrationService.findGoogleAdsAdGroups({
                                    integrationId: integration?.id, adGroupIds: ids
                                });

                                for (const item of result?.records) {
                                    for (const i of iResult) {
                                        if (parseInt(i?.id) === parseInt(item?.name)) {
                                            item.trafficSourceName = i?.name;
                                            if (i?.status === "ENABLED" || i?.status === "PAUSED") {
                                                item.status = i?.active;
                                            }
                                        }
                                    }
                                }
                            } catch (e) {
                                dispatch(alertActions.error(e?.response?.data?.error));
                            }
                        }
                    }

                    if (groupBy3Placeholder === "{creative}") {
                        let ids = "";
                        for (const item of result?.records) {
                            if (item?.name?.match(/^\d+$/) !== null) {
                                if (ids === "") {
                                    ids += item?.name;
                                } else {
                                    ids += "," + item?.name;
                                }
                            }
                        }

                        // find Google Ads ad group ads if group by placeholder 3 is {creative}
                        if (ids !== "") {
                            try {
                                let iResult = await integrationService.findGoogleAdsAdGroupAds({
                                    integrationId: integration?.id, adIds: ids
                                });

                                for (const item of result?.records) {
                                    for (const i of iResult) {
                                        if (parseInt(i?.id) === parseInt(item?.name)) {
                                            item.trafficSourceName = i?.name;
                                            item.trafficSourceAdGroupId = i?.adGroupId;
                                            if (i?.status === "ENABLED" || i?.status === "PAUSED") {
                                                item.status = i?.active;
                                            }
                                        }
                                    }
                                }
                            } catch (e) {
                                dispatch(alertActions.error(e?.response?.data?.error));
                            }
                        }
                    }
                }

                // Add Microsoft Ads check
                if (integration?.networkType === "microsoft") {

                    let campaignId = GetSelectedPlaceholderNameValue({
                        getState, groupBy1Placeholder, groupBy2Placeholder,
                        treeViewData: groupBy1Data,
                        detail1ParentUUID: params?.level1RowUUID,
                        detail2ParentUUID: params?.parentRowUUID,
                        isTreeView: params?.isTreeView,
                        placeholder: "{CampaignId}",
                    });

                    let adGroupId = GetSelectedPlaceholderNameValue({
                        getState, groupBy1Placeholder, groupBy2Placeholder,
                        treeViewData: groupBy1Data,
                        detail1ParentUUID: params?.level1RowUUID,
                        detail2ParentUUID: params?.parentRowUUID,
                        isTreeView: params?.isTreeView,
                        placeholder: "{AdGroupId}",
                    });

                    // find Microsoft Ads campaigns if group by 3 placeholder is {CampaignId}
                    if (groupBy3Placeholder === "{CampaignId}") {
                        let ids = "";
                        for (const item of result?.records) {
                            if (item?.name?.match(/^\d+$/) !== null) {
                                if (ids === "") {
                                    ids += item?.name;
                                } else {
                                    ids += "," + item?.name;
                                }
                            }
                        }

                        // find Microsoft Ads campaigns if group by placeholder 3 is {CampaignId}
                        if (ids !== "") {
                            try {
                                let iResult = await integrationService.findMicrosoftAdsCampaigns({
                                    integrationId: integration?.id, campaignIds: ids
                                });

                                for (const item of result?.records) {
                                    for (const i of iResult) {
                                        if (parseInt(i?.id) === parseInt(item?.name)) {
                                            item.trafficSourceName = i?.name;
                                            if (i?.status === "Active" || i?.status === "Paused") {
                                                item.status = i?.active;
                                            }
                                        }
                                    }
                                }
                            } catch (e) {
                                dispatch(alertActions.error(e?.response?.data?.error));
                            }
                        }
                    }

                    if (groupBy3Placeholder === "{AdGroupId}" && campaignId !== null) {
                        let ids = "";
                        for (const item of result?.records) {
                            if (item?.name?.match(/^\d+$/) !== null) {
                                if (ids === "") {
                                    ids += item?.name;
                                } else {
                                    ids += "," + item?.name;
                                }
                            }
                        }

                        // find Microsoft Ads ad groups if group by placeholder 3 is {AdGroupId}
                        if (ids !== "") {
                            try {
                                let iResult = await integrationService.findMicrosoftAdsAdGroups({
                                    integrationId: integration?.id, adGroupIds: ids, campaignId: campaignId
                                });

                                for (const item of result?.records) {
                                    for (const i of iResult) {
                                        if (parseInt(i?.id) === parseInt(item?.name)) {
                                            item.trafficSourceName = i?.name;
                                            if (i?.status === "Active" || i?.status === "Paused") {
                                                item.status = i?.active;
                                            }
                                        }
                                    }
                                }
                            } catch (e) {
                                dispatch(alertActions.error(e?.response?.data?.error));
                            }
                        }
                    }

                    if (groupBy3Placeholder === "{AdId}" && adGroupId !== null) {
                        let ids = "";
                        for (const item of result?.records) {
                            if (item?.name?.match(/^\d+$/) !== null) {
                                if (ids === "") {
                                    ids += item?.name;
                                } else {
                                    ids += "," + item?.name;
                                }
                            }
                        }

                        // find Microsoft Ads ad group ads if group by placeholder 3 is {AdId}
                        if (ids !== "") {
                            try {
                                let iResult = await integrationService.findMicrosoftAdsAdGroupAds({
                                    integrationId: integration?.id, adIds: ids, adGroupId: adGroupId
                                });

                                for (const item of result?.records) {
                                    for (const i of iResult) {
                                        if (parseInt(i?.id) === parseInt(item?.name)) {
                                            item.trafficSourceName = i?.name;
                                            item.trafficSourceAdGroupId = i?.adGroupId;
                                            if (i?.status === "Active" || i?.status === "Paused") {
                                                item.status = i?.active;
                                            }
                                        }
                                    }
                                }
                            } catch (e) {
                                dispatch(alertActions.error(e?.response?.data?.error));
                            }
                        }
                    }
                }

                // Add Facebook Ads integration check.
                if (integration?.networkType === "facebook") {
                    let ids = "";
                    for (const item of result?.records) {
                        if (item?.name?.match(/^\d+$/) !== null) {
                            if (ids === "") {
                                ids += item?.name;
                            } else {
                                ids += "," + item?.name;
                            }
                        }
                    }

                    // find Facebook Ads campaign, ad set and ad titles.
                    if (ids !== "" && ["{{campaign.id}}", "{{adset.id}}", "{{ad.id}}"].indexOf(groupBy3Placeholder) !== -1) {

                        let level = "campaign";
                        if (groupBy3Placeholder === "{{adset.id}}") {
                            level = "adset";
                        }
                        if (groupBy3Placeholder === "{{ad.id}}") {
                            level = "ad";
                        }

                        try {
                            let iResult = await integrationService.findFacebookMetricsAndOtherFields({
                                integrationId: integration?.id,
                                level: level,
                                ids: ids,
                                dateStart: params?.from,
                                dateStop: params?.to
                            });
                            for (const item of result?.records) {
                                if (iResult?.insights?.length > 0) {
                                    for (const i of iResult?.insights) {
                                        if (i?.campaignId === item?.name) {
                                            item.trafficSourceName = i?.campaignName;
                                            item.fbCpm = i?.cpm;
                                            item.fbImpressions = i?.impressions;
                                            item.fbLinkClicks = i?.linkClicks;
                                            item.fbLinkCpc = i?.linkCpc;
                                            item.fbCtr = i?.linkCtr;
                                            item.fbAccountCurrency = i?.accountCurrency;
                                            item.fbEpc = parseFloat(item?.revenue) / parseInt(i?.linkClicks);
                                            break;
                                        } else if (i?.adSetId === item?.name) {
                                            item.trafficSourceName = i?.adSetName;
                                            item.fbCpm = i?.cpm;
                                            item.fbImpressions = i?.impressions;
                                            item.fbLinkClicks = i?.linkClicks;
                                            item.fbLinkCpc = i?.linkCpc;
                                            item.fbCtr = i?.linkCtr;
                                            item.fbAccountCurrency = i?.accountCurrency;
                                            item.fbEpc = parseFloat(item?.revenue) / parseInt(i?.linkClicks);
                                            break;
                                        } else if (i?.adId === item?.name) {
                                            item.trafficSourceName = i?.adName;
                                            item.fbCpm = i?.cpm;
                                            item.fbImpressions = i?.impressions;
                                            item.fbLinkClicks = i?.linkClicks;
                                            item.fbLinkCpc = i?.linkCpc;
                                            item.fbCtr = i?.linkCtr;
                                            item.fbAccountCurrency = i?.accountCurrency;
                                            item.fbEpc = parseFloat(item?.revenue) / parseInt(i?.linkClicks);
                                            break;
                                        }
                                    }
                                }
                                if (iResult?.campaigns?.length > 0) {
                                    for (const c of iResult?.campaigns) {
                                        if (c?.id === item?.name) {
                                            item.trafficSourceName = c?.name;
                                            item.status = c?.status;
                                            item.fbDailyBudget = c?.dailyBudget;
                                            item.deliveryStatus = c?.deliveryStatus;
                                            break;
                                        }
                                    }
                                }
                                if (iResult?.adSets?.length > 0) {
                                    for (const adSet of iResult?.adSets) {
                                        if (adSet?.id === item?.name) {
                                            item.trafficSourceName = adSet?.name;
                                            item.status = adSet?.status;
                                            item.fbDailyBudget = adSet?.dailyBudget;
                                            item.deliveryStatus = adSet?.deliveryStatus;
                                            if (adSet?.bidStrategy === "LOWEST_COST_WITH_BID_CAP" || adSet?.bidStrategy === "COST_CAP") {
                                                item.bid = adSet?.bidAmount;
                                                item.rateModel = "Bid";
                                            }
                                            if (adSet?.bidStrategy === "" && adSet?.bidAmount > 0) {
                                                item.bid = adSet?.bidAmount;
                                                item.rateModel = "Bid";
                                            }
                                            break;
                                        }
                                    }
                                }
                                if (iResult?.ads?.length > 0) {
                                    for (const ad of iResult?.ads) {
                                        if (ad?.id === item?.name) {
                                            item.trafficSourceName = ad?.name;
                                            item.status = ad?.status;
                                            item.fbDailyBudget = ad?.dailyBudget;
                                            item.deliveryStatus = ad?.deliveryStatus;
                                            break;
                                        }
                                    }
                                }
                            }
                        } catch (e) {
                            dispatch(alertActions.error(e?.response?.data?.error));
                        }
                    }

                    // Find Facebook Ads stats when group by 3 is Date
                    let facebookId = null;
                    let level = "campaign";

                    if (["{{campaign.id}}", "{{adset.id}}", "{{ad.id}}"].indexOf(groupBy2Placeholder) !== -1) {
                        facebookId = GetSelectedValue(getState().groupBy2?.selectedRows);
                        // Get parent row UUID for tree view.
                        if (params?.isTreeView) {
                            groupBy1Data?.records?.forEach((record) => {
                                if (record?.uuid === params?.parentRowUUID) {
                                    facebookId = record?.name;
                                }
                            })
                        }
                        if (groupBy2Placeholder === "{{adset.id}}") {
                            level = "adset";
                        } else if (groupBy2Placeholder === "{{ad.id}}") {
                            level = "ad";
                        }
                    } else if (["{{campaign.id}}", "{{adset.id}}", "{{ad.id}}"].indexOf(groupBy1Placeholder) !== -1) {
                        facebookId = GetSelectedValue(getState().groupBy1?.selectedRows);
                        // Get parent row UUID for tree view.
                        if (params?.isTreeView) {
                            groupBy1Data?.records?.forEach((record) => {
                                if (record?.uuid === params?.topParentId) {
                                    facebookId = record?.name;
                                }
                            })
                        }
                        if (groupBy1Placeholder === "{{adset.id}}") {
                            level = "adset";
                        } else if (groupBy1Placeholder === "{{ad.id}}") {
                            level = "ad";
                        }
                    }

                    if (facebookId !== null && groupBy3 === "date") {
                        try {
                            let iResult = await integrationService.findFacebookMetricsAndOtherFields({
                                integrationId: integration?.id,
                                level: level,
                                ids: facebookId,
                                dateStart: params?.from,
                                dateStop: params?.to,
                                breakdownByDay: "1"
                            });
                            for (const item of result?.records) {
                                if (iResult?.insights?.length > 0) {
                                    for (const i of iResult?.insights) {
                                        if (i?.dateStart === item?.name) {
                                            item.fbCpm = i?.cpm;
                                            item.fbImpressions = i?.impressions;
                                            item.fbLinkClicks = i?.linkClicks;
                                            item.fbLinkCpc = i?.linkCpc;
                                            item.fbCtr = i?.linkCtr;
                                            item.fbAccountCurrency = i?.accountCurrency;
                                            item.fbEpc = parseFloat(item?.revenue) / parseInt(i?.linkClicks);
                                        }
                                    }
                                }
                            }
                        } catch (e) {
                            dispatch(alertActions.error(e?.response?.data?.error));
                        }
                    }
                }

                // Add Taboola integration check.
                if (integration?.networkType === "taboola") {
                    let campaignId = GetSelectedPlaceholderNameValue({
                        getState, groupBy1Placeholder, groupBy2Placeholder,
                        treeViewData: groupBy1Data,
                        detail1ParentUUID: params?.level1RowUUID,
                        detail2ParentUUID: params?.parentRowUUID,
                        isTreeView: params?.isTreeView,
                        placeholder: "{campaign_id}",
                    });

                    // find Taboola campaigns if group by 3 placeholder is {campaign_id}
                    if (groupBy3Placeholder === "{campaign_id}") {
                        try {
                            let iResult = await integrationService.findTaboolaCampaigns({
                                integrationId: integration?.id
                            });

                            for (const item of result?.records) {
                                for (const i of iResult) {
                                    if (parseInt(i?.id) === parseInt(item?.name)) {
                                        item.trafficSourceName = i?.name;
                                        item.status = i?.active;
                                        item.deliveryStatus = i?.status;
                                        item.fbDailyBudget = i?.dailyBudget;
                                    }
                                }
                            }
                        } catch (e) {
                            dispatch(alertActions.error(e?.response?.data?.error));
                        }
                    }

                    if ((groupBy1Placeholder === "{campaign_id}" || groupBy2Placeholder === "{campaign_id}") && groupBy3Placeholder === "{campaign_item_id}") {
                        // find Google Ads ad group ads if group by placeholder 3 is {campaign_item_id}
                        try {
                            let iResult = await integrationService.findTaboolaCampaignAds({
                                integrationId: integration?.id, campaignId: campaignId
                            });

                            for (const item of result?.records) {
                                for (const i of iResult) {
                                    if (parseInt(i?.id) === parseInt(item?.name)) {
                                        item.trafficSourceName = i?.title;
                                        if (i?.status === "RUNNING" || i?.status === "PAUSED" || i?.status === "PENDING_APPROVAL") {
                                            item.status = i?.active;
                                        }
                                    }
                                }
                            }
                        } catch (e) {
                            dispatch(alertActions.error(e?.response?.data?.error));
                        }
                    }

                    if ((groupBy1Placeholder === "{campaign_id}" || groupBy2Placeholder === "{campaign_id}") && groupBy3Placeholder === "{site}") {
                        // find Google Ads ad group ads if group by placeholder 3 is {campaign_item_id}
                        try {
                            let iResult = await integrationService.findTaboolaCampaign({
                                integrationId: integration?.id, campaignId: campaignId
                            });

                            for (const item of result?.records) {
                                item.status = true;
                                // Set bid
                                if ((iResult?.bid_strategy === "SMART" || iResult?.bid_strategy === "FIXED") && iResult?.cpc !== null) {
                                    item.bid = iResult?.cpc;
                                    for (const i of iResult?.publisher_bid_modifier?.values) {
                                        if (i?.target === item?.name) {
                                            item.bid = i?.cpc_modification * iResult?.cpc;
                                        }
                                    }
                                }
                                // Set site status false
                                if (iResult?.publisher_targeting.value.includes(item?.name) && iResult?.publisher_targeting.type === "EXCLUDE") {
                                    item.status = false;
                                }
                                // Set site status false
                                if (!iResult?.publisher_targeting.value.includes(item?.name) && iResult?.publisher_targeting.type === "INCLUDE") {
                                    item.status = false;
                                }
                            }
                        } catch (e) {
                            dispatch(alertActions.error(e?.response?.data?.error));
                        }
                    }
                }

                // Add MGID integration check.
                if (integration?.networkType === "mgid") {

                    let campaignId = GetSelectedPlaceholderNameValue({
                        getState, groupBy1Placeholder, groupBy2Placeholder,
                        treeViewData: groupBy1Data,
                        detail1ParentUUID: params?.level1RowUUID,
                        detail2ParentUUID: params?.parentRowUUID,
                        isTreeView: params?.isTreeView,
                        placeholder: "{campaign_id}",
                    });

                    if (groupBy3Placeholder === "{campaign_id}") {
                        let ids = "";
                        for (const item of result?.records) {
                            if (item?.name?.match(/^\d+$/) !== null) {
                                if (ids === "") {
                                    ids += item?.name;
                                } else {
                                    ids += "," + item?.name;
                                }
                            }
                        }

                        if (ids !== "") {
                            try {
                                let iResult = await integrationService.findMgidCampaigns({
                                    integrationId: integration?.id, campaignIds: ids
                                });

                                for (const item of result?.records) {
                                    for (const i of iResult) {
                                        if (parseInt(i?.id) === parseInt(item?.name)) {
                                            item.trafficSourceName = i?.name;
                                            item.status = i?.active;
                                            item.deliveryStatus = i?.deliveryStatus;
                                            break;
                                        }
                                    }
                                }
                            } catch (e) {
                                dispatch(alertActions.error(e?.response?.data?.error));
                            }
                        }
                    }

                    if (groupBy3Placeholder === "{teaser_id}") {
                        let ids = "";
                        for (const item of result?.records) {
                            if (item?.name?.match(/^\d+$/) !== null) {
                                if (ids === "") {
                                    ids += item?.name;
                                } else {
                                    ids += "," + item?.name;
                                }
                            }
                        }

                        if (ids !== "") {
                            try {
                                let iResult = await integrationService.findMgidAds({
                                    integrationId: integration?.id, adIds: ids
                                });
                                for (const item of result?.records) {
                                    for (const i of iResult) {
                                        if (parseInt(i?.id) === parseInt(item?.name)) {
                                            item.status = i?.active;
                                            item.deliveryStatus = i?.deliveryStatus
                                            let bidNum = i?.bid / 100;
                                            item.bid = bidNum.toFixed(4);
                                            break;
                                        }
                                    }
                                }
                            } catch (e) {
                                dispatch(alertActions.error(e?.response?.data?.error));
                            }
                        }
                    }

                    if (campaignId !== null && IsNumber(campaignId) && groupBy3Placeholder === "{widget_id}") {
                        try {
                            let iResult = await integrationService.findMgidWidgets({
                                integrationId: integration?.id, campaignId: campaignId
                            });

                            for (const item of result?.records) {
                                if (iResult?.filterType === "off") {
                                    item.status = true;
                                } else if (iResult?.filterType === "except") {
                                    item.status = !iResult?.widgets?.includes(item?.name);
                                } else if (iResult?.filterType === "only") {
                                    if (iResult?.widgets?.includes(item?.name)) {
                                        item.status = true;
                                    } else {
                                        item.status = false;
                                    }
                                }
                            }
                        } catch (e) {
                            dispatch(alertActions.error(e?.response?.data?.error));
                        }
                    }
                }

                // Add Outbrain Ads check
                if (integration?.networkType === "outbrain") {

                    const outbrainRegex = /^[a-z0-9]{34}$/

                    let campaignId = null;
                    let campaignValue = GetSelectedPlaceholderNameValue({
                        getState, groupBy1Placeholder, groupBy2Placeholder,
                        treeViewData: groupBy1Data,
                        detail1ParentUUID: params?.level1RowUUID,
                        detail2ParentUUID: params?.parentRowUUID,
                        isTreeView: params?.isTreeView,
                        placeholder: "{{campaign_id}}",
                    });
                    if (campaignValue?.match(outbrainRegex) !== null) {
                        campaignId = campaignValue;
                    }

                    if (groupBy3Placeholder === "{{campaign_id}}") {

                        // Filter campaign IDs that match 34 lowercase alphanumeric chars AND start with "00"
                        const ids = result?.records
                            ?.filter(item => /^[a-z0-9]{34}$/.test(item?.name) && item.name.startsWith("00"))
                            .map(item => item.name)
                            .join(",") || "";

                        if (ids !== "") {
                            try {
                                let iResult = await integrationService.findOutbrainCampaigns({
                                    integrationId: integration?.id,
                                    campaignIds: ids
                                });

                                for (const item of result?.records) {
                                    for (const i of iResult) {
                                        if (i?.id === item?.name) {
                                            item.trafficSourceName = i?.name;
                                            item.status = i?.status;
                                            item.deliveryStatus = i?.deliveryStatus;
                                            item.bid = i?.bid;
                                            item.bidRate = i?.bidRate;
                                            item.budgetId = i?.budget?.id;
                                            item.fbDailyBudget = i?.budget?.amount;
                                            item.fbAccountCurrency = i?.budget?.currency;
                                            break;
                                        }
                                    }
                                }
                            } catch (e) {
                                dispatch(alertActions.error(e?.response?.data?.error));
                            }
                        }
                    }

                    if (groupBy3Placeholder === "{{ad_id}}" && campaignId !== null) {

                        let ids = "";
                        for (const item of result?.records) {
                            if (item?.name?.match(outbrainRegex) !== null) {
                                if (ids === "") {
                                    ids += item?.name;
                                } else {
                                    ids += "," + item?.name;
                                }
                            }
                        }

                        if (ids !== "") {
                            try {
                                let iResult = await integrationService.findOutbrainAds({
                                    integrationId: integration?.id, campaignId: campaignId
                                });
                                for (const item of result?.records) {
                                    for (const i of iResult) {
                                        if (i?.id === item?.name) {
                                            item.trafficSourceName = i?.name;
                                            item.status = i?.active;
                                            item.deliveryStatus = i?.deliveryStatus
                                            break;
                                        }
                                    }
                                }
                            } catch (e) {
                                dispatch(alertActions.error(e?.response?.data?.error));
                            }
                        }
                    }

                    if (groupBy3Placeholder === "{{section_id}}" && campaignId !== null) {

                        let sectionIds = "";
                        for (const item of result?.records) {
                            if (item?.name?.match(/^[a-z0-9]{34}$/) !== null) {
                                if (sectionIds === "") {
                                    sectionIds += item?.name;
                                } else {
                                    sectionIds += "," + item?.name;
                                }
                            }
                        }

                        if (sectionIds?.length > 0) {
                            try {
                                let iSections = await integrationService.findOutbrainSections({
                                    integrationId: integration?.id, sectionIds: sectionIds
                                });
                                for (const item of result?.records) {
                                    if (item?.name?.match(outbrainRegex) === null) {
                                        // Skip
                                        continue;
                                    }
                                    // Add section name
                                    for (const oSection of iSections?.sections) {
                                        if (oSection?.id === item?.name) {
                                            item.trafficSourceName = oSection?.name;
                                            break;
                                        }
                                    }
                                }
                            } catch (e) {
                                dispatch(alertActions.error(e?.response?.data?.error));
                            }
                        }

                        try {
                            let iResult = await integrationService.findOutbrainCampaigns({
                                integrationId: integration?.id, campaignIds: campaignId
                            });
                            for (const item of result?.records) {
                                if (item?.name?.match(outbrainRegex) === null) {
                                    // Skip
                                    continue;
                                }
                                item.status = true;
                                item.deliveryStatus = "Active";
                                for (const oCampaign of iResult) {
                                    if (oCampaign?.id === campaignId) {

                                        if (oCampaign?.allowBidsBySection) {
                                            item.adjustCpcPercentage = 0;
                                            // Edit section bid value
                                            if (oCampaign?.bidsBySection?.length > 0) {
                                                for (const bidBySec of oCampaign?.bidsBySection) {
                                                    if (bidBySec?.id === item?.name) {
                                                        item.adjustCpcPercentage = bidBySec?.bid * 100;
                                                        break;
                                                    }
                                                }
                                            }
                                        }

                                        // Edit section status
                                        if (oCampaign?.blockedSections?.includes(item?.name)) {
                                            item.status = false;
                                            item.deliveryStatus = "Paused";
                                            break;
                                        }
                                        break;
                                    }
                                }
                            }
                        } catch (e) {
                            dispatch(alertActions.error(e?.response?.data?.error));
                        }
                    }

                    if (groupBy3Placeholder === "{{publisher_id}}" && campaignId !== null) {
                        try {
                            let iResult = await integrationService.findOutbrainCampaigns({
                                integrationId: integration?.id, campaignIds: campaignId
                            });
                            for (const item of result?.records) {
                                if (item?.name?.match(outbrainRegex) === null) {
                                    // Skip
                                    continue;
                                }
                                item.status = true;
                                item.deliveryStatus = "Active";
                                for (const oCampaign of iResult) {
                                    if (oCampaign?.id === campaignId) {
                                        // Edit section status
                                        if (oCampaign?.blockedPublishers?.includes(item?.name)) {
                                            item.status = false;
                                            item.deliveryStatus = "Paused";
                                            break;
                                        }
                                        break;
                                    }
                                }
                            }
                        } catch (e) {
                            dispatch(alertActions.error(e?.response?.data?.error));
                        }
                    }
                }

                // Add TikTok Ads integration check.
                if (integration?.networkType === "tiktok") {
                    let ids = "";
                    for (const item of result?.records) {
                        if (item?.name?.match(/^\d+$/) !== null) {
                            if (ids === "") {
                                ids += item?.name;
                            } else {
                                ids += "," + item?.name;
                            }
                        }
                    }

                    let tikTokPlaceholders = ["__CAMPAIGN_ID__", "__AID__", "__CID__"];

                    // Find TikTok campaign, adgroup and ad titles.
                    if (ids !== "" && tikTokPlaceholders.indexOf(groupBy3Placeholder) !== -1) {

                        let level = "campaign";
                        if (groupBy3Placeholder === "__AID__") {
                            level = "adgroup";
                        } else if (groupBy3Placeholder === "__CID__") {
                            level = "ad";
                        }

                        try {
                            let iResult = await integrationService.findTikTokMetricsAndOtherFields({
                                integrationId: integration?.id,
                                level: level,
                                ids: ids,
                                dateStart: params?.from,
                                dateStop: params?.to
                            });
                            for (const item of result?.records) {
                                if (iResult?.insights?.length > 0) {
                                    for (const i of iResult?.insights) {
                                        if (parseInt(i?.campaignId) === parseInt(item?.name)) {
                                            item.trafficSourceName = i?.campaignName;
                                            item.fbCpm = i?.cpm;
                                            item.fbImpressions = i?.impressions;
                                            item.fbLinkClicks = i?.linkClicks;
                                            item.fbLinkCpc = i?.linkCpc;
                                            item.fbCtr = i?.linkCtr;
                                            item.fbAccountCurrency = i?.accountCurrency;
                                            item.fbEpc = parseFloat(item?.revenue) / parseInt(i?.linkClicks);
                                            break;
                                        } else if (parseInt(i?.adGroupId) === parseInt(item?.name)) {
                                            item.trafficSourceName = i?.adGroupName;
                                            item.fbCpm = i?.cpm;
                                            item.fbImpressions = i?.impressions;
                                            item.fbLinkClicks = i?.linkClicks;
                                            item.fbLinkCpc = i?.linkCpc;
                                            item.fbCtr = i?.linkCtr;
                                            item.fbAccountCurrency = i?.accountCurrency;
                                            item.fbEpc = parseFloat(item?.revenue) / parseInt(i?.linkClicks);
                                            break;
                                        } else if (parseInt(i?.adId) === parseInt(item?.name)) {
                                            item.trafficSourceName = i?.adName;
                                            item.fbCpm = i?.cpm;
                                            item.fbImpressions = i?.impressions;
                                            item.fbLinkClicks = i?.linkClicks;
                                            item.fbLinkCpc = i?.linkCpc;
                                            item.fbCtr = i?.linkCtr;
                                            item.fbAccountCurrency = i?.accountCurrency;
                                            item.fbEpc = parseFloat(item?.revenue) / parseInt(i?.linkClicks);
                                            break;
                                        }
                                    }
                                }
                                if (iResult?.campaigns?.length > 0) {
                                    for (const c of iResult?.campaigns) {
                                        if (parseInt(c?.id) === parseInt(item?.name)) {
                                            item.trafficSourceName = c?.name;
                                            item.status = c?.status;
                                            if (c?.dailyBudget > 0) {
                                                item.fbDailyBudget = c?.dailyBudget;
                                            }
                                            item.deliveryStatus = c?.deliveryStatus;
                                            break;
                                        }
                                    }
                                }
                                if (iResult?.adGroups?.length > 0) {
                                    for (const adGroup of iResult?.adGroups) {
                                        if (parseInt(adGroup?.id) === parseInt(item?.name)) {
                                            item.trafficSourceName = adGroup?.name;
                                            item.status = adGroup?.status;
                                            if (adGroup?.dailyBudget > 0) {
                                                item.fbDailyBudget = adGroup?.dailyBudget;
                                            }
                                            item.deliveryStatus = adGroup?.deliveryStatus;
                                            break;
                                        }
                                    }
                                }
                                if (iResult?.ads?.length > 0) {
                                    for (const ad of iResult?.ads) {
                                        if (parseInt(ad?.id) === parseInt(item?.name)) {
                                            item.trafficSourceName = ad?.name;
                                            item.status = ad?.status;
                                            item.fbDailyBudget = ad?.dailyBudget;
                                            item.deliveryStatus = ad?.deliveryStatus;
                                            break;
                                        }
                                    }
                                }
                            }
                        } catch (e) {
                            if (e?.response?.data?.error) {
                                dispatch(alertActions.error(e?.response?.data?.error));
                            } else {
                                dispatch(alertActions.error("Unable to load TikTok metrics data."));
                            }
                        }
                    }

                    // Find TikTok stats when group by 3 is Date
                    let tikTokId = null;
                    let level = "campaign";

                    if (tikTokPlaceholders.indexOf(groupBy2Placeholder) !== -1) {
                        tikTokId = GetSelectedValue(getState().groupBy2?.selectedRows);
                        if (groupBy2Placeholder === "__AID__") {
                            level = "adgroup";
                        } else if (groupBy2Placeholder === "__CID__") {
                            level = "ad";
                        }
                    } else if (tikTokPlaceholders.indexOf(groupBy1Placeholder) !== -1) {
                        tikTokId = GetSelectedValue(getState().groupBy1?.selectedRows);
                        if (groupBy1Placeholder === "__AID__") {
                            level = "adgroup";
                        } else if (groupBy1Placeholder === "__CID__") {
                            level = "ad";
                        }
                    }

                    // Find TikTok stats when group by 3 is Date
                    if (tikTokId !== null && groupBy3 === "date") {

                        try {
                            let iResult = await integrationService.findTikTokMetricsAndOtherFields({
                                integrationId: integration?.id,
                                level: level,
                                ids: tikTokId,
                                dateStart: params?.from,
                                dateStop: params?.to,
                                breakdownByDay: "1"
                            });
                            for (const item of result?.records) {
                                if (iResult?.insights?.length > 0) {
                                    for (const i of iResult?.insights) {
                                        if (i?.dateStart?.slice(0, 10) === item?.name) {
                                            item.fbCpm = i?.cpm;
                                            item.fbImpressions = i?.impressions;
                                            item.fbLinkClicks = i?.linkClicks;
                                            item.fbLinkCpc = i?.linkCpc;
                                            item.fbCtr = i?.linkCtr;
                                            item.fbAccountCurrency = i?.accountCurrency;
                                            item.fbEpc = parseFloat(item?.revenue) / parseInt(i?.linkClicks);
                                        }
                                    }
                                }
                            }
                        } catch (e) {
                            dispatch(alertActions.error(e?.response?.data?.error));
                        }
                    }
                }

                // Add RevContent Ads check
                if (integration?.networkType === "revcontent") {

                    const revContentRegex = /^\d+$/

                    let campaignId = null;
                    let campaignValue = GetSelectedPlaceholderNameValue({
                        getState, groupBy1Placeholder, groupBy2Placeholder,
                        treeViewData: groupBy1Data,
                        detail1ParentUUID: params?.level1RowUUID,
                        detail2ParentUUID: params?.parentRowUUID,
                        isTreeView: params?.isTreeView,
                        placeholder: "{boost_id}",
                    });
                    if (campaignValue?.match(revContentRegex) !== null) {
                        campaignId = campaignValue;
                    }

                    if (groupBy3Placeholder === "{boost_id}") {
                        let ids = "";
                        for (const item of result?.records) {
                            if (item?.name?.match(revContentRegex) !== null) {
                                if (ids === "") {
                                    ids += item?.name;
                                } else {
                                    ids += "," + item?.name;
                                }
                            }
                        }

                        if (ids !== "") {
                            try {
                                let iResult = await integrationService.findRevContentCampaigns({
                                    integrationId: integration?.id,
                                    campaignIds: ids
                                });

                                for (const item of result?.records) {
                                    for (const i of iResult) {
                                        if (parseInt(i?.id) === parseInt(item?.name)) {
                                            item.trafficSourceName = i?.name;
                                            item.status = i?.active;
                                            item.deliveryStatus = i?.deliveryStatus;
                                            item.bid = i?.bid;
                                            item.bidRate = i?.bidRate;
                                            item.fbDailyBudget = i?.dailyBudget;
                                            item.fbAccountCurrency = i?.currency;
                                            break;
                                        }
                                    }
                                }
                            } catch (e) {
                                dispatch(alertActions.error(e?.response?.data?.error));
                            }
                        }
                    }

                    if (groupBy3Placeholder === "{content_id}" && campaignId !== null) {

                        let ids = "";
                        for (const item of result?.records) {
                            if (item?.name?.match(revContentRegex) !== null) {
                                if (ids === "") {
                                    ids += item?.name;
                                } else {
                                    ids += "," + item?.name;
                                }
                            }
                        }

                        if (ids !== "") {
                            try {
                                let iResult = await integrationService.findRevContentContents({
                                    integrationId: integration?.id,
                                    campaignId: campaignId,
                                    dateStart: params?.from,
                                    dateStop: params?.to
                                });
                                for (const item of result?.records) {
                                    for (const i of iResult) {
                                        if (parseInt(i?.id) === parseInt(item?.name)) {
                                            item.trafficSourceName = i?.name;
                                            item.status = i?.active;
                                            item.deliveryStatus = i?.deliveryStatus;
                                            item.fbImpressions = i?.impressions;
                                            break;
                                        }
                                    }
                                }
                            } catch (e) {
                                dispatch(alertActions.error(e?.response?.data?.error));
                            }
                        }
                    }

                    if (groupBy3Placeholder === "{widget_id}" && campaignId !== null) {

                        let ids = "";
                        for (const item of result?.records) {
                            if (item?.name?.match(revContentRegex) !== null) {
                                if (ids === "") {
                                    ids += item?.name;
                                } else {
                                    ids += "," + item?.name;
                                }
                            }
                        }

                        if (ids !== "") {
                            try {
                                let iResult = await integrationService.findRevContentWidgets({
                                    integrationId: integration?.id,
                                    campaignId: campaignId,
                                    dateStart: params?.from,
                                    dateStop: params?.to
                                });
                                for (const item of result?.records) {
                                    for (const i of iResult) {
                                        if (parseInt(i?.id) === parseInt(item?.name)) {
                                            item.trafficSourceName = i?.name;
                                            item.status = i?.active;
                                            item.bid = i?.bid;
                                            item.fbImpressions = i?.impressions;
                                            break;
                                        }
                                    }
                                }
                            } catch (e) {
                                dispatch(alertActions.error(e?.response?.data?.error));
                            }
                        }
                    }
                }

                // Add Outbrain DSP Ads check
                if (integration?.networkType === "outbrain_dsp") {

                    const outbrainDspRegex = /^\d+$/

                    let adGroupId = null;
                    let adGroupValue = GetSelectedPlaceholderNameValue({
                        getState, groupBy1Placeholder, groupBy2Placeholder,
                        treeViewData: groupBy1Data,
                        detail1ParentUUID: params?.level1RowUUID,
                        detail2ParentUUID: params?.parentRowUUID,
                        isTreeView: params?.isTreeView,
                        placeholder: "{adgroupid}",
                    });
                    if (adGroupValue?.match(outbrainDspRegex) !== null) {
                        adGroupId = adGroupValue;
                    }

                    if (groupBy3Placeholder === "{campaignid}") {
                        let ids = "";
                        for (const item of result?.records) {
                            if (item?.name?.match(outbrainDspRegex) !== null) {
                                if (ids === "") {
                                    ids += item?.name;
                                } else {
                                    ids += "," + item?.name;
                                }
                            }
                        }

                        if (ids !== "") {
                            try {
                                let iResult = await integrationService.findOutbrainDSPCampaigns({
                                    integrationId: integration?.id,
                                    campaignIds: ids
                                });

                                for (const item of result?.records) {
                                    for (const i of iResult) {
                                        if (i?.id === item?.name) {
                                            item.trafficSourceName = i?.name;
                                            item.deliveryStatus = i?.deliveryStatus;
                                            break;
                                        }
                                    }
                                }
                            } catch (e) {
                                dispatch(alertActions.error(e?.response?.data?.error));
                            }
                        }
                    }

                    if (groupBy3Placeholder === "{adgroupid}") {
                        let ids = "";
                        for (const item of result?.records) {
                            if (item?.name?.match(outbrainDspRegex) !== null) {
                                if (ids === "") {
                                    ids += item?.name;
                                } else {
                                    ids += "," + item?.name;
                                }
                            }
                        }

                        if (ids !== "") {
                            try {
                                let iResult = await integrationService.findOutbrainDSPAdGroups({
                                    integrationId: integration?.id,
                                    adGroupIds: ids
                                });

                                for (const item of result?.records) {
                                    for (const i of iResult) {
                                        if (i?.id === item?.name) {
                                            item.trafficSourceName = i?.name;
                                            item.status = i?.status;
                                            item.deliveryStatus = i?.deliveryStatus;
                                            item.bid = i?.bid;
                                            item.bidRate = i?.bidRate;
                                            item.fbDailyBudget = i?.dailyBudgetAmount;
                                            item.fbAccountCurrency = i?.dailyBudgetCurrency;
                                            break;
                                        }
                                    }
                                }
                            } catch (e) {
                                dispatch(alertActions.error(e?.response?.data?.error));
                            }
                        }
                    }

                    // Add buttons for ads
                    if (groupBy3Placeholder === "{contentadid}" && adGroupId !== null) {

                        let ids = "";
                        for (const item of result?.records) {
                            if (item?.name?.match(outbrainDspRegex) !== null) {
                                if (ids === "") {
                                    ids += item?.name;
                                } else {
                                    ids += "," + item?.name;
                                }
                            }
                        }

                        if (ids !== "") {
                            try {
                                let iResult = await integrationService.findOutbrainDSPAdGroupAd({
                                    integrationId: integration?.id, adGroupId: adGroupId
                                });
                                for (const item of result?.records) {
                                    for (const i of iResult) {
                                        if (i?.id === item?.name) {
                                            item.trafficSourceName = i?.name;
                                            item.status = i?.active;
                                            item.deliveryStatus = i?.deliveryStatus
                                            break;
                                        }
                                    }
                                }
                            } catch (e) {
                                dispatch(alertActions.error(e?.response?.data?.error));
                            }
                        }
                    }

                    // Add buttons for publishers
                    if (groupBy3Placeholder === "{publisher}" && adGroupId !== null) {
                        try {
                            let iResult = await integrationService.findOutbrainDSPAdGroups({
                                integrationId: integration?.id, adGroupIds: adGroupId
                            });
                            for (const item of result?.records) {
                                if (item?.name?.match(/^\d+$/) === null) {
                                    // Skip
                                    continue;
                                }
                                item.status = true;
                                item.deliveryStatus = "Active";
                                for (const pub of iResult) {
                                    if (parseInt(pub?.id) === parseInt(adGroupId)) {
                                        // Edit publishers status
                                        if (pub?.blackListedPublishers?.includes(parseInt(item?.name))) {
                                            item.status = false;
                                            item.deliveryStatus = "Paused";
                                            break;
                                        }
                                        break;
                                    }
                                }
                            }
                        } catch (e) {
                            dispatch(alertActions.error(e?.response?.data?.error));
                        }
                    }
                }

                // Add Newsbreak integration check
                if (integration?.networkType === "newsbreak") {
                    let ids = "";
                    for (const item of result?.records) {
                        if (item?.name?.match(/^\d+$/) !== null) {
                            if (ids === "") {
                                ids += item?.name;
                            } else {
                                ids += "," + item?.name;
                            }
                        }
                    }

                    if (groupBy3Placeholder === "__CAMPAIGN_ID__" && ids !== "") {
                        try {
                            let iResult = await integrationService.findNewsBreakCampaigns({
                                integrationId: integration?.id
                            });
                            for (const item of result?.records) {
                                for (const i of iResult) {
                                    if (i?.id === item?.name) {
                                        item.trafficSourceName = i?.name;
                                        item.status = i?.status;
                                        item.deliveryStatus = i?.deliveryStatus;
                                        break;
                                    }
                                }
                            }
                        } catch (e) {
                            dispatch(alertActions.error(e?.response?.data?.error));
                        }
                    }

                    if (groupBy3Placeholder === "__FLIGHT_ID__" && ids !== "") {
                        try {
                            let iResult = await integrationService.findNewsBreakAdSets({
                                integrationId: integration?.id
                            });
                            for (const item of result?.records) {
                                for (const i of iResult) {
                                    if (i?.id === item?.name) {
                                        item.trafficSourceName = i?.name;
                                        item.status = i?.status;
                                        item.deliveryStatus = i?.deliveryStatus;
                                        item.fbDailyBudget = i?.dailyBudgetAmount;
                                        item.fbAccountCurrency = i?.dailyBudgetCurrency;

                                        if (i?.rateModel === "TARGET_CPA") {
                                            item.bid = i?.bid;
                                            item.rateModel = "CPA";
                                        }

                                        break;
                                    }
                                }
                            }
                        } catch (e) {
                            dispatch(alertActions.error(e?.response?.data?.error));
                        }
                    }

                    if (groupBy3Placeholder === "__CREATIVE_ID__" && ids !== "") {
                        try {
                            let iResult = await integrationService.findNewsBreakAds({
                                integrationId: integration?.id
                            });
                            for (const item of result?.records) {
                                for (const i of iResult) {
                                    if (i?.id === item?.name) {
                                        item.trafficSourceName = i?.name;
                                        item.status = i?.status;
                                        item.deliveryStatus = i?.deliveryStatus;
                                        break;
                                    }
                                }
                            }
                        } catch (e) {
                            dispatch(alertActions.error(e?.response?.data?.error));
                        }
                    }
                }

                // Add SmartNews integration check
                if (integration?.networkType === "smartnews") {
                    let ids = "";
                    for (const item of result?.records) {
                        if (item?.name?.match(/^\d+$/) !== null) {
                            if (ids === "") {
                                ids += item?.name;
                            } else {
                                ids += "," + item?.name;
                            }
                        }
                    }

                    let smartnewsPlaceholders = ["{campaign_id}", "{ad_group_id}", "{ad_id}"];

                    // Find TikTok campaign, adgroup and ad titles.
                    if (ids !== "" && smartnewsPlaceholders.indexOf(groupBy3Placeholder) !== -1) {

                        let level = "campaigns";
                        if (groupBy3Placeholder === "{ad_group_id}") {
                            level = "ad_groups";
                        } else if (groupBy3Placeholder === "{ad_id}") {
                            level = "ads";
                        }

                        try {
                            let iResult = await integrationService.findSmartNewsMetricsAndAdditionalColumns({
                                integrationId: integration?.id,
                                level: level,
                                ids: ids,
                                dateStart: params?.from,
                                dateStop: params?.to
                            });
                            for (const item of result?.records) {
                                if (iResult?.insights?.length > 0) {
                                    for (const i of iResult?.insights) {
                                        if (i?.campaignId === item?.name) {
                                            item.trafficSourceName = i?.campaignName;
                                            item.fbCpm = i?.cpm;
                                            item.fbImpressions = i?.impressions;
                                            item.fbLinkClicks = i?.linkClicks;
                                            item.fbLinkCpc = i?.linkCpc;
                                            item.fbCtr = i?.linkCtr;
                                            item.fbAccountCurrency = i?.accountCurrency;
                                            item.fbEpc = parseFloat(item?.revenue) / parseInt(i?.linkClicks);

                                            item.status = i?.status;
                                            if (i?.dailyBudget > 0) {
                                                item.fbDailyBudget = i?.dailyBudget;
                                            }
                                            item.deliveryStatus = i?.deliveryStatus;
                                            break;
                                        } else if (i?.adGroupId === item?.name) {
                                            item.trafficSourceName = i?.adGroupName;
                                            item.fbCpm = i?.cpm;
                                            item.fbImpressions = i?.impressions;
                                            item.fbLinkClicks = i?.linkClicks;
                                            item.fbLinkCpc = i?.linkCpc;
                                            item.fbCtr = i?.linkCtr;
                                            item.fbAccountCurrency = i?.accountCurrency;
                                            item.fbEpc = parseFloat(item?.revenue) / parseInt(i?.linkClicks);

                                            item.status = i?.status;
                                            item.deliveryStatus = i?.deliveryStatus;
                                            break;
                                        } else if (i?.adId === item?.name) {
                                            item.trafficSourceName = i?.adName;
                                            item.fbCpm = i?.cpm;
                                            item.fbImpressions = i?.impressions;
                                            item.fbLinkClicks = i?.linkClicks;
                                            item.fbLinkCpc = i?.linkCpc;
                                            item.fbCtr = i?.linkCtr;
                                            item.fbAccountCurrency = i?.accountCurrency;
                                            item.fbEpc = parseFloat(item?.revenue) / parseInt(i?.linkClicks);

                                            item.status = i?.status;
                                            item.deliveryStatus = i?.deliveryStatus;
                                            break;
                                        }
                                    }
                                }
                            }
                        } catch (e) {
                            if (e?.response?.data?.error) {
                                dispatch(alertActions.error(e?.response?.data?.error));
                            } else {
                                dispatch(alertActions.error("Unable to load TikTok metrics data."));
                            }
                        }
                    }
                }

                // Add MediaGo Ads check
                if (integration?.networkType === "mediago") {

                    const mediaGoRegex = /^\d+$/;

                    let campaignId = GetSelectedPlaceholderNameValue({
                        getState, groupBy1Placeholder, groupBy2Placeholder,
                        treeViewData: groupBy1Data,
                        detail1ParentUUID: params?.level1RowUUID,
                        detail2ParentUUID: params?.parentRowUUID,
                        isTreeView: params?.isTreeView,
                        // eslint-disable-next-line no-template-curly-in-string
                        placeholder: "${CAMPAIGN_ID}",
                    });

                    // find MediaGo campaigns if group by 2 placeholder is ${CAMPAIGN_ID}
                    // eslint-disable-next-line no-template-curly-in-string
                    if (groupBy3Placeholder === "${CAMPAIGN_ID}") {
                        let ids = "";
                        for (const item of result?.records) {
                            if (item?.name?.match(mediaGoRegex) !== null) {
                                if (ids === "") {
                                    ids += item?.name;
                                } else {
                                    ids += "," + item?.name;
                                }
                            }
                        }

                        if (ids !== "") {
                            try {
                                let iResult = await integrationService.findMediaGoMetricsAndAdditionalColumns({
                                    integrationId: integration?.id,
                                    level: "campaign",
                                    ids: ids,
                                    dateStart: params?.from,
                                    dateStop: params?.to
                                });
                                for (const item of result?.records) {
                                    if (iResult?.insights?.length > 0) {
                                        for (const i of iResult?.insights) {
                                            if (i?.campaignId === item?.name) {
                                                item.trafficSourceName = i?.campaignName;
                                                item.fbImpressions = i?.impressions;
                                                item.fbLinkClicks = i?.linkClicks;
                                                item.fbAccountCurrency = i?.accountCurrency;
                                                item.fbEpc = parseFloat(item?.revenue) / parseInt(i?.linkClicks);
                                                item.bid = i?.linkCpc;

                                                item.status = i?.status;
                                                if (i?.dailyBudget > 0) {
                                                    item.fbDailyBudget = i?.dailyBudget;
                                                }
                                                item.deliveryStatus = i?.deliveryStatus;
                                                break;
                                            }
                                        }
                                    }
                                }
                            } catch (e) {
                                if (e?.response?.data?.error) {
                                    dispatch(alertActions.error(e?.response?.data?.error));
                                } else {
                                    dispatch(alertActions.error("Unable to load MediaGo metrics and additional columns data."));
                                }
                            }
                        }
                    }

                    // find MediaGo ads if group by 2 placeholder is ${AD_ID}
                    // eslint-disable-next-line no-template-curly-in-string
                    if ((groupBy1Placeholder === "${CAMPAIGN_ID}" || groupBy2Placeholder === "${CAMPAIGN_ID}") && groupBy3Placeholder === "${AD_ID}") {

                        try {
                            let iResult = await integrationService.findMediaGoMetricsAndAdditionalColumns({
                                integrationId: integration?.id,
                                level: "ad",
                                ids: campaignId,
                                dateStart: params?.from,
                                dateStop: params?.to
                            });
                            for (const item of result?.records) {
                                if (iResult?.insights?.length > 0) {
                                    for (const i of iResult?.insights) {
                                        if (i?.adId === item?.name) {
                                            item.trafficSourceName = i?.adName;
                                            item.fbImpressions = i?.impressions;
                                            item.fbLinkClicks = i?.linkClicks;
                                            item.fbAccountCurrency = i?.accountCurrency;
                                            item.fbEpc = parseFloat(item?.revenue) / parseInt(i?.linkClicks);
                                            item.status = i?.status;
                                            item.deliveryStatus = i?.deliveryStatus;
                                            break;
                                        }
                                    }
                                }
                            }
                        } catch (e) {
                            if (e?.response?.data?.error) {
                                dispatch(alertActions.error(e?.response?.data?.error));
                            } else {
                                dispatch(alertActions.error("Unable to load MediaGo metrics and additional columns data."));
                            }
                        }
                    }

                    // find MediaGo sites if group by 2 placeholder is ${SITE_ID}
                    // eslint-disable-next-line no-template-curly-in-string
                    if ((groupBy1Placeholder === "${CAMPAIGN_ID}" || groupBy2Placeholder === "${CAMPAIGN_ID}") && groupBy3Placeholder === "${SITE_ID}") {

                        try {
                            let iResult = await integrationService.findMediaGoMetricsAndAdditionalColumns({
                                integrationId: integration?.id,
                                level: "site",
                                ids: campaignId,
                                dateStart: params?.from,
                                dateStop: params?.to
                            });
                            for (const item of result?.records) {
                                if (iResult?.insights?.length > 0) {
                                    for (const i of iResult?.insights) {
                                        if (i?.siteId === item?.name) {
                                            item.trafficSourceName = i?.siteName;
                                            item.fbImpressions = i?.impressions;
                                            item.fbLinkClicks = i?.linkClicks;
                                            item.fbAccountCurrency = i?.accountCurrency;
                                            item.fbEpc = parseFloat(item?.revenue) / parseInt(i?.linkClicks);
                                            item.status = i?.status;
                                            item.deliveryStatus = i?.deliveryStatus;
                                            item.bid = i?.siteCpc;
                                            break;
                                        }
                                    }
                                }
                            }
                        } catch (e) {
                            if (e?.response?.data?.error) {
                                dispatch(alertActions.error(e?.response?.data?.error));
                            } else {
                                dispatch(alertActions.error("Unable to load MediaGo metrics and additional columns data."));
                            }
                        }
                    }
                }

                // Add MediaGo Ads check
                if (integration?.networkType === "popin") {

                    const mediaGoRegex = /^\d+$/;

                    let campaignId = GetSelectedPlaceholderNameValue({
                        getState, groupBy1Placeholder, groupBy2Placeholder,
                        treeViewData: groupBy1Data,
                        detail1ParentUUID: params?.level1RowUUID,
                        detail2ParentUUID: params?.parentRowUUID,
                        isTreeView: params?.isTreeView,
                        // eslint-disable-next-line no-template-curly-in-string
                        placeholder: "${CAMPAIGN_ID}",
                    });

                    // find Popin campaigns if group by 2 placeholder is ${CAMPAIGN_ID}
                    // eslint-disable-next-line no-template-curly-in-string
                    if (groupBy3Placeholder === "${CAMPAIGN_ID}") {
                        let ids = "";
                        for (const item of result?.records) {
                            if (item?.name?.match(mediaGoRegex) !== null) {
                                if (ids === "") {
                                    ids += item?.name;
                                } else {
                                    ids += "," + item?.name;
                                }
                            }
                        }

                        if (ids !== "") {
                            try {
                                let iResult = await integrationService.findPopinMetricsAndAdditionalColumns({
                                    integrationId: integration?.id,
                                    level: "campaign",
                                    ids: ids,
                                    dateStart: params?.from,
                                    dateStop: params?.to
                                });
                                for (const item of result?.records) {
                                    if (iResult?.insights?.length > 0) {
                                        for (const i of iResult?.insights) {
                                            if (i?.campaignId === item?.name) {
                                                item.trafficSourceName = i?.campaignName;
                                                item.fbImpressions = i?.impressions;
                                                item.fbLinkClicks = i?.linkClicks;
                                                item.fbAccountCurrency = i?.accountCurrency;
                                                item.fbEpc = parseFloat(item?.revenue) / parseInt(i?.linkClicks);
                                                item.bid = i?.linkCpc;

                                                item.status = i?.status;
                                                if (i?.dailyBudget > 0) {
                                                    item.fbDailyBudget = i?.dailyBudget;
                                                }
                                                item.deliveryStatus = i?.deliveryStatus;
                                                break;
                                            }
                                        }
                                    }
                                }
                            } catch (e) {
                                if (e?.response?.data?.error) {
                                    dispatch(alertActions.error(e?.response?.data?.error));
                                } else {
                                    dispatch(alertActions.error("Unable to load Popin metrics and additional columns data."));
                                }
                            }
                        }
                    }

                    // find Popin sites if group by 2 placeholder is ${SITE_ID}
                    // eslint-disable-next-line no-template-curly-in-string
                    if ((groupBy1Placeholder === "${CAMPAIGN_ID}" || groupBy2Placeholder === "${CAMPAIGN_ID}") && groupBy3Placeholder === "${SITE_ID}") {

                        try {
                            let iResult = await integrationService.findPopinMetricsAndAdditionalColumns({
                                integrationId: integration?.id,
                                level: "site",
                                ids: campaignId,
                                dateStart: params?.from,
                                dateStop: params?.to
                            });
                            for (const item of result?.records) {
                                if (iResult?.insights?.length > 0) {
                                    for (const i of iResult?.insights) {
                                        if (i?.siteId === item?.name) {
                                            item.trafficSourceName = i?.siteName;
                                            item.fbImpressions = i?.impressions;
                                            item.fbLinkClicks = i?.linkClicks;
                                            item.fbAccountCurrency = i?.accountCurrency;
                                            item.fbEpc = parseFloat(item?.revenue) / parseInt(i?.linkClicks);
                                            item.status = i?.status;
                                            item.deliveryStatus = i?.deliveryStatus;
                                            item.bid = i?.siteCpc;
                                            break;
                                        }
                                    }
                                }
                            }
                        } catch (e) {
                            if (e?.response?.data?.error) {
                                dispatch(alertActions.error(e?.response?.data?.error));
                            } else {
                                dispatch(alertActions.error("Unable to load Popin metrics and additional columns data."));
                            }
                        }
                    }
                }

                // Set modified result.
                if (infinityScroll) {
                    dispatch({type: groupBy3Constants.GROUP_BY_3_INFINITY_SCROLL, result});
                } else {
                    dispatch({type: groupBy3Constants.GROUP_BY_3_GET_DATA, result});

                    // TreeView logic to map Group By 3 data in to Group By 1 data.
                    if (params?.isTreeView && params?.parentRowUUID !== undefined) {
                        if (params?.loadMore) {
                            dispatch({type: groupBy1Constants.GROUP_BY_1_LOAD_MORE_BUTTON, payload: {clickedRowUUID: params?.clickedRowUUID, records: result?.records}});
                        } else {
                            dispatch({type: groupBy1Constants.GROUP_BY_1_EXPAND_ROW, payload: {clickedRowUUID: params?.clickedRowUUID, records: result?.records, expandStatus: params?.expand}});
                        }
                    }
                }
            }).catch((error) => {
                dispatch({
                    type: params?.isTreeView
                        ? groupBy1Constants.GROUP_BY_1_ERROR
                        : groupBy3Constants.GROUP_BY_3_ERROR,
                    error
                });
                if (error?.response?.data?.error) {
                    dispatch(alertActions.error(error?.response?.data?.error));
                } else {
                    dispatch(alertActions.error("Invalid request. Please change your Group By values."));
                }
            }
        );
    };
}

function updateOrderBy(orderBy) {
    return (dispatch) => {
        dispatch({type: groupBy3Constants.GROUP_BY_3_UPDATE_ORDER_BY, orderBy});
    };
}

function updateSortBy(sortBy) {
    return (dispatch) => {
        dispatch({type: groupBy3Constants.GROUP_BY_3_UPDATE_SORT_BY, sortBy});
    };
}

function updateSortColumns(sortColumns) {
    return (dispatch) => {
        dispatch({type: groupBy3Constants.GROUP_BY_3_UPDATE_SORT_COLUMNS, sortColumns});
    };
}

function rerender() {
    return dispatch => {
        dispatch({type: groupBy3Constants.GROUP_BY_3_RERENDER});
    };
}

function setSelectedRows(selectedUUIDs, selectedRows) {
    return dispatch => {
        dispatch({type: groupBy3Constants.GROUP_BY_3_SET_SELECTED_ROW, payload: {selectedUUIDs, selectedRows}});
    };
}

function updateGroupBy(groupBy) {
    return dispatch => {
        dispatch({type: groupBy3Constants.GROUP_BY_3_UPDATE_GROUP_BY, groupBy});
    };
}

function GetSelectedPlaceholderNameValue(
    {
        getState, groupBy1Placeholder, groupBy2Placeholder, treeViewData, detail1ParentUUID, detail2ParentUUID,
        isTreeView, placeholder
    }
) {
    const isGroupBy1 = groupBy1Placeholder === placeholder;
    const isGroupBy2 = groupBy2Placeholder === placeholder;

    if (!isGroupBy1 && !isGroupBy2) {
        return null;
    }

    // Select correct groupBy state
    const selectedValue = GetSelectedValue(getState()[isGroupBy1 ? "groupBy1" : "groupBy2"]?.selectedRows);

    if (!isTreeView) {
        return selectedValue
    }

    // Find matching parent in tree view
    const parentUUID = isGroupBy1 ? detail1ParentUUID : detail2ParentUUID;
    const parentRecord = treeViewData?.records?.find(r => r?.uuid === parentUUID);

    return parentRecord?.name || selectedValue;
}

function GetTopParentUUID(records, parentRowUUID) {
    const record = records?.find(r => r?.uuid === parentRowUUID);
    if (!record) return null;

    switch (record.type) {
        case LEVEL_1:
            return record.uuid;
        case LEVEL_2:
            return record.parentId;
        case LEVEL_3: {
            const parent = records?.find(r => r?.uuid === record.parentId);
            return parent?.parentId || null;
        }
        default:
            return null;
    }
}

function updateStatus({status, name, dataLevel, isTreeView = false, parentRowUUID = ""}) {
    return (dispatch, getState) => {

        let result = getState().groupBy3.data;
        // Override data source if it is tree view reporting.
        if (isTreeView) {
            result = getState().groupBy1.data;
        }

        let integration = getState().groupBy1.integration;
        let trafficSource = getState().groupBy1.trafficSource;
        let groupBy1 = getState().groupBy1.groupBy?.groupBy;
        let groupBy2 = getState().groupBy2.groupBy?.groupBy;
        let groupBy3 = getState().groupBy3.groupBy?.groupBy;
        let groupBy1Placeholder = GetSelectedGroupByPlaceholderFromTrafficSource(groupBy1, trafficSource);
        let groupBy2Placeholder = GetSelectedGroupByPlaceholderFromTrafficSource(groupBy2, trafficSource);

        // Get current group by placeholder
        let currentPlaceholder = GetSelectedGroupByPlaceholderFromTrafficSource(groupBy3, trafficSource);
        if (dataLevel === LEVEL_1) {
            currentPlaceholder = groupBy1Placeholder;
        } else if (dataLevel === LEVEL_2) {
            currentPlaceholder = groupBy2Placeholder;
        }

        const topParentUUID = GetTopParentUUID(result?.records, parentRowUUID);

        const startSpinner = () => {
            dispatch({
                type: isTreeView
                    ? groupBy1Constants.GROUP_BY_1_LOADING_SPINNER
                    : groupBy3Constants.GROUP_BY_3_LOADING_SPINNER,
            });
            dispatch(alertActions.error(""));
        }

        const stopSpinner = (e) => {
            dispatch(alertActions.error(e?.response?.data?.error));
            dispatch({
                type: isTreeView
                    ? groupBy1Constants.GROUP_BY_1_STOP_LOADING_SPINNER
                    : groupBy3Constants.GROUP_BY_3_STOP_LOADING_SPINNER,
            });
        }

        const updateData = (updatedResult) => {
            dispatch({
                type: isTreeView
                    ? groupBy1Constants.GROUP_BY_1_GET_DATA
                    : groupBy3Constants.GROUP_BY_3_GET_DATA,
                result: updatedResult,
            });
            dispatch(alertActions.success("Updated successfully"));
        }

        if (integration?.networkType === "pushground") {

            let pushgroundCampaignId = GetSelectedPlaceholderNameValue({
                getState, groupBy1Placeholder, groupBy2Placeholder,
                treeViewData: result,
                detail1ParentUUID: topParentUUID,
                detail2ParentUUID: parentRowUUID,
                isTreeView: isTreeView,
                placeholder: "{campaign_id}",
            });

            if (currentPlaceholder === "{campaign_id}") {
                startSpinner();
                integrationService.updatePushgroundCampaignStatus(
                    status, integration?.id, name
                ).then(() => {
                    const updatedRecords = result?.records?.map((record) => {
                        if (record?.name === name) {
                            return {...record, status: status};
                        }
                        return record;
                    });
                    const updatedResult = {...result, records: updatedRecords};
                    updateData(updatedResult);
                }).catch((e) => {
                    stopSpinner(e);
                });
            }

            if (currentPlaceholder === "{creativity_id}") {
                startSpinner();
                integrationService.updatePushgroundCreativityStatus(
                    status, integration?.id, name
                ).then(() => {
                    const updatedRecords = result?.records?.map((record) => {
                        if (record?.name === name) {
                            return {...record, status: status};
                        }
                        return record;
                    });
                    const updatedResult = {...result, records: updatedRecords};
                    updateData(updatedResult);
                }).catch((e) => {
                    stopSpinner(e);
                });
            }

            if (currentPlaceholder === "{supply_id}" && IsNumber(pushgroundCampaignId)) {
                startSpinner();
                integrationService.updatePushgroundCampaignSupplyAndSiteStatus(
                    status, integration?.id, pushgroundCampaignId, name, null
                ).then(() => {
                    const updatedRecords = result?.records?.map((record) => {
                        if (record?.name === name) {
                            return {...record, status: status};
                        }
                        return record;
                    });
                    const updatedResult = {...result, records: updatedRecords};
                    updateData(updatedResult);
                }).catch((e) => {
                    stopSpinner(e);
                });
            }

            if (currentPlaceholder === "{site_id}" && IsNumber(pushgroundCampaignId)) {
                let supplySite = name.split(/-(.*)/s);
                let supplyId = supplySite[0];
                let siteId = supplySite[1];

                if (siteId === undefined || siteId === "" || siteId === null) {
                    dispatch(alertActions.error("Updating Site status failed. Invalid Site ID."));
                    return false;
                }

                startSpinner();
                integrationService.updatePushgroundCampaignSupplyAndSiteStatus(
                    status, integration?.id, pushgroundCampaignId, supplyId, siteId
                ).then(() => {
                    const updatedRecords = result?.records?.map((record) => {
                        if (record?.name === name) {
                            return {...record, status: status};
                        }
                        return record;
                    });
                    const updatedResult = {...result, records: updatedRecords};
                    updateData(updatedResult);
                }).catch((e) => {
                    stopSpinner(e);
                });
            }
        }

        if (integration?.networkType === "propeller-ads") {

            let campaignId = GetSelectedPlaceholderNameValue({
                getState, groupBy1Placeholder, groupBy2Placeholder,
                treeViewData: result,
                detail1ParentUUID: topParentUUID,
                detail2ParentUUID: parentRowUUID,
                isTreeView: isTreeView,
                placeholder: "{campaignid}",
            });

            if (currentPlaceholder === "{zoneid}" && campaignId !== null) {
                startSpinner();
                integrationService.updatePropellerAdsZoneStatus({
                    status: status, integrationId: integration?.id, campaignId: campaignId, zoneId: name
                }).then(() => {
                    const updatedRecords = result?.records?.map((record) => {
                        if (record?.name === name) {
                            return {...record, status: status};
                        }
                        return record;
                    });
                    const updatedResult = {...result, records: updatedRecords};
                    updateData(updatedResult);
                }).catch((e) => {
                    stopSpinner(e);
                });
            }

            if (currentPlaceholder === "{campaignid}") {
                startSpinner();
                integrationService.updatePropellerAdsCampaignStatus({
                    status: status, integrationId: integration?.id, campaignId: name
                }).then(() => {
                    const updatedRecords = result?.records?.map((record) => {
                        if (record?.name === name) {
                            return {...record, status: status};
                        }
                        return record;
                    });
                    const updatedResult = {...result, records: updatedRecords};
                    updateData(updatedResult);
                }).catch((e) => {
                    stopSpinner(e);
                });
            }

            if (currentPlaceholder === "{bannerid}") {
                startSpinner();
                integrationService.updatePropellerAdsBannerStatus({
                    status,
                    integrationId: integration?.id,
                    bannerId: name
                }).then((response) => {
                    const updatedRecords = result?.records?.map((record) => {
                        if (record?.name === name) {
                            return {...record, status: response?.active};
                        }
                        return record;
                    });
                    const updatedResult = {...result, records: updatedRecords};
                    updateData(updatedResult);
                }).catch((e) => {
                    stopSpinner(e);
                });
            }
        }

        if (integration?.networkType === "exoclick") {
            let campaignId = GetSelectedPlaceholderNameValue({
                getState, groupBy1Placeholder, groupBy2Placeholder,
                treeViewData: result,
                detail1ParentUUID: topParentUUID,
                detail2ParentUUID: parentRowUUID,
                isTreeView: isTreeView,
                placeholder: "{campaign_id}",
            });

            if (currentPlaceholder === "{campaign_id}") {
                startSpinner();
                integrationService.updateExoClickCampaignStatus({
                    status: status, integrationId: integration?.id, campaignId: name
                }).then(() => {
                    const updatedRecords = result?.records?.map((record) => {
                        if (record?.name === name) {
                            return {...record, status: status};
                        }
                        return record;
                    });
                    const updatedResult = {...result, records: updatedRecords};
                    updateData(updatedResult);
                }).catch((e) => {
                    stopSpinner(e);
                });
            }
            if (currentPlaceholder === "{variation_id}") {
                startSpinner();
                integrationService.updateExoClickBannerStatus({
                    campaignId: campaignId,
                    status,
                    integrationId: integration?.id,
                    bannerId: name
                }).then((response) => {
                    const updatedRecords = result?.records?.map((record) => {
                        if (record?.name === name) {
                            return {...record, status: response?.active};
                        }
                        return record;
                    });
                    const updatedResult = {...result, records: updatedRecords};
                    updateData(updatedResult);
                }).catch((e) => {
                    stopSpinner(e);
                });
            }
            if (currentPlaceholder === "{zone_id}") {
                startSpinner();
                integrationService.updateExoClickZoneStatus({
                    status: status, integrationId: integration?.id, campaignId: campaignId, zoneId: name
                }).then(() => {
                    const updatedRecords = result?.records?.map((record) => {
                        if (record?.name === name) {
                            return {...record, status: status};
                        }
                        return record;
                    });
                    const updatedResult = {...result, records: updatedRecords};
                    updateData(updatedResult);
                }).catch((e) => {
                    stopSpinner(e);
                });
            }
            if (currentPlaceholder === "{site_id}") {
                startSpinner();
                integrationService.updateExoClickSiteStatus({
                    status: status, integrationId: integration?.id, campaignId: campaignId, siteId: name
                }).then(() => {
                    const updatedRecords = result?.records?.map((record) => {
                        if (record?.name === name) {
                            return {...record, status: status};
                        }
                        return record;
                    });
                    const updatedResult = {...result, records: updatedRecords};
                    updateData(updatedResult);
                }).catch((e) => {
                    stopSpinner(e);
                });
            }
        }

        if (integration?.networkType === "mondiad") {

            let campaignId = GetSelectedPlaceholderNameValue({
                getState, groupBy1Placeholder, groupBy2Placeholder,
                treeViewData: result,
                detail1ParentUUID: topParentUUID,
                detail2ParentUUID: parentRowUUID,
                isTreeView: isTreeView,
                placeholder: "[campaignid]",
            });

            if (currentPlaceholder === "[campaignid]") {
                startSpinner();
                integrationService.updateMondiadCampaignStatus({
                    status: status, integrationId: integration?.id, campaignId: name
                }).then(() => {
                    const updatedRecords = result?.records?.map((record) => {
                        if (record?.name === name) {
                            return {...record, status: status};
                        }
                        return record;
                    });
                    const updatedResult = {...result, records: updatedRecords};
                    updateData(updatedResult);
                }).catch((e) => {
                    stopSpinner(e);
                });
            }
            if (currentPlaceholder === "[creativeid]") {
                startSpinner();
                integrationService.updateMondiadCreativeStatus({
                    status,
                    integrationId: integration?.id,
                    creativeId: name
                }).then((response) => {
                    const updatedRecords = result?.records?.map((record) => {
                        if (record?.name === name) {
                            return {...record, status: response?.active};
                        }
                        return record;
                    });
                    const updatedResult = {...result, records: updatedRecords};
                    updateData(updatedResult);
                }).catch((e) => {
                    stopSpinner(e);
                });
            }
            if (currentPlaceholder === "[zoneid]") {
                startSpinner();
                integrationService.updateMondiadZoneStatus({
                    status: status, integrationId: integration?.id, campaignId: campaignId, zoneId: parseInt(name)
                }).then(() => {
                    const updatedRecords = result?.records?.map((record) => {
                        if (record?.name === name) {
                            return {...record, status: status};
                        }
                        return record;
                    });
                    const updatedResult = {...result, records: updatedRecords};
                    updateData(updatedResult);
                }).catch((e) => {
                    stopSpinner(e);
                });
            }
            if (currentPlaceholder === "[subid]") {
                startSpinner();
                integrationService.updateMondiadSiteStatus({
                    status: status, integrationId: integration?.id, campaignId: campaignId, siteId: name
                }).then(() => {
                    const updatedRecords = result?.records?.map((record) => {
                        if (record?.name === name) {
                            return {...record, status: status};
                        }
                        return record;
                    });
                    const updatedResult = {...result, records: updatedRecords};
                    updateData(updatedResult);
                }).catch((e) => {
                    stopSpinner(e);
                });
            }
        }

        if (integration?.networkType === "google") {
            if (currentPlaceholder === "{campaignid}") {
                startSpinner();
                integrationService.updateGoogleAdsCampaignStatus({
                    status: status, integrationId: integration?.id, campaignId: name
                }).then(() => {
                    const updatedRecords = result?.records?.map((record) => {
                        if (record?.name === name) {
                            return {...record, status: status};
                        }
                        return record;
                    });
                    const updatedResult = {...result, records: updatedRecords};
                    updateData(updatedResult);
                }).catch((e) => {
                    stopSpinner(e);
                });
            }
            if (currentPlaceholder === "{adgroupid}") {
                startSpinner();
                integrationService.updateGoogleAdsAdGroupStatus({
                    status: status, integrationId: integration?.id, adGroupId: name
                }).then(() => {
                    const updatedRecords = result?.records?.map((record) => {
                        if (record?.name === name) {
                            return {...record, status: status};
                        }
                        return record;
                    });
                    const updatedResult = {...result, records: updatedRecords};
                    updateData(updatedResult);
                }).catch((e) => {
                    stopSpinner(e);
                });
            }
            if (currentPlaceholder === "{creative}") {
                startSpinner();

                let adGroupId = 0;
                for (let i of result?.records) {
                    if (i?.trafficSourceAdGroupId > 0) {
                        adGroupId = i?.trafficSourceAdGroupId;
                        break;
                    }
                }

                integrationService.updateGoogleAdsAdGroupAdStatus({
                    status: status, integrationId: integration?.id, adGroupId, adId: name
                }).then(() => {
                    const updatedRecords = result?.records?.map((record) => {
                        if (record?.name === name) {
                            return {...record, status: status};
                        }
                        return record;
                    });
                    const updatedResult = {...result, records: updatedRecords};
                    updateData(updatedResult);
                }).catch((e) => {
                    stopSpinner(e);
                });
            }
        }

        if (integration?.networkType === "microsoft") {

            let campaignId = GetSelectedPlaceholderNameValue({
                getState, groupBy1Placeholder, groupBy2Placeholder,
                treeViewData: result,
                detail1ParentUUID: topParentUUID,
                detail2ParentUUID: parentRowUUID,
                isTreeView: isTreeView,
                placeholder: "{CampaignId}",
            });

            let adGroupId = GetSelectedPlaceholderNameValue({
                getState, groupBy1Placeholder, groupBy2Placeholder,
                treeViewData: result,
                detail1ParentUUID: topParentUUID,
                detail2ParentUUID: parentRowUUID,
                isTreeView: isTreeView,
                placeholder: "{AdGroupId}",
            });

            if (currentPlaceholder === "{CampaignId}") {
                startSpinner();
                integrationService.updateMicrosoftAdsCampaignStatus({
                    status: status, integrationId: integration?.id, campaignId: name
                }).then(() => {
                    const updatedRecords = result?.records?.map((record) => {
                        if (record?.name === name) {
                            return {...record, status: status};
                        }
                        return record;
                    });
                    const updatedResult = {...result, records: updatedRecords};
                    updateData(updatedResult);
                }).catch((e) => {
                    stopSpinner(e);
                });
            }
            if (currentPlaceholder === "{AdGroupId}" && IsNumber(campaignId)) {
                startSpinner();
                integrationService.updateMicrosoftAdsAdGroupStatus({
                    status: status, integrationId: integration?.id, campaignId: campaignId, adGroupId: name
                }).then(() => {
                    const updatedRecords = result?.records?.map((record) => {
                        if (record?.name === name) {
                            return {...record, status: status};
                        }
                        return record;
                    });
                    const updatedResult = {...result, records: updatedRecords};
                    updateData(updatedResult);
                }).catch((e) => {
                    stopSpinner(e);
                });
            }
            if (currentPlaceholder === "{AdId}" && IsNumber(adGroupId)) {
                startSpinner();

                // Get Microsoft Ads Type
                let adType = ""
                for (let i of result?.records) {
                    if (i?.name === name) {
                        adType = i?.trafficSourceName;
                        break;
                    }
                }

                integrationService.updateMicrosoftAdsAdStatus({
                    status: status, integrationId: integration?.id, adId: name, adGroupId: adGroupId, adType: adType
                }).then(() => {
                    const updatedRecords = result?.records?.map((record) => {
                        if (record?.name === name) {
                            return {...record, status: status};
                        }
                        return record;
                    });
                    const updatedResult = {...result, records: updatedRecords};
                    updateData(updatedResult);
                }).catch((e) => {
                    stopSpinner(e);
                });
            }
        }

        if (integration?.networkType === "taboola") {

            let campaignId = GetSelectedPlaceholderNameValue({
                getState, groupBy1Placeholder, groupBy2Placeholder,
                treeViewData: result,
                detail1ParentUUID: topParentUUID,
                detail2ParentUUID: parentRowUUID,
                isTreeView: isTreeView,
                placeholder: "{campaign_id}",
            });

            if (currentPlaceholder === "{campaign_id}" && IsNumber(name)) {
                startSpinner();
                integrationService.updateTaboolaCampaignStatus({
                    status: status, integrationId: integration?.id, campaignId: name
                }).then(() => {
                    const updatedRecords = result?.records?.map((record) => {
                        if (record?.name === name) {
                            return {...record, status: status};
                        }
                        return record;
                    });
                    const updatedResult = {...result, records: updatedRecords};
                    updateData(updatedResult);
                }).catch((e) => {
                    stopSpinner(e);
                });
            }

            if (currentPlaceholder === "{campaign_item_id}" && IsNumber(campaignId) && IsNumber(name)) {
                startSpinner();
                integrationService.updateTaboolaCampaignAdStatus({
                    status: status, integrationId: integration?.id, campaignId: campaignId, adId: name
                }).then(() => {
                    const updatedRecords = result?.records?.map((record) => {
                        if (record?.name === name) {
                            return {...record, status: status};
                        }
                        return record;
                    });
                    const updatedResult = {...result, records: updatedRecords};
                    updateData(updatedResult);
                }).catch((e) => {
                    stopSpinner(e);
                });
            }

            if (currentPlaceholder === "{site}" && IsNumber(campaignId)) {
                startSpinner();
                integrationService.updateTaboolaCampaignSiteStatus({
                    status: status, integrationId: integration?.id, campaignId: campaignId, site: name
                }).then(() => {
                    const updatedRecords = result?.records?.map((record) => {
                        if (record?.name === name) {
                            return {...record, status: status};
                        }
                        return record;
                    });
                    const updatedResult = {...result, records: updatedRecords};
                    updateData(updatedResult);
                }).catch((e) => {
                    stopSpinner(e);
                });
            }
        }

        if (integration?.networkType === "facebook") {
            let fbPlaceholders = ["{{campaign.id}}", "{{adset.id}}", "{{ad.id}}"]
            if (fbPlaceholders.indexOf(currentPlaceholder) !== -1) {
                startSpinner();
                integrationService.updateFacebookStatus({
                    status: status, integrationId: integration?.id, id: name
                }).then((response) => {
                    const updatedRecords = result?.records?.map((record) => {
                        if (record?.name === name) {
                            const isActive = response?.success === true && status;
                            return {
                                ...record,
                                status: status,
                                deliveryStatus: isActive ? "Active" : "Paused",
                            };
                        }
                        return record;
                    });
                    const updatedResult = {...result, records: updatedRecords};
                    updateData(updatedResult);
                }).catch((e) => {
                    stopSpinner(e);
                });
            }
        }

        if (integration?.networkType === "mgid") {

            let campaignId = GetSelectedPlaceholderNameValue({
                getState, groupBy1Placeholder, groupBy2Placeholder,
                treeViewData: result,
                detail1ParentUUID: topParentUUID,
                detail2ParentUUID: parentRowUUID,
                isTreeView: isTreeView,
                placeholder: "{campaign_id}",
            });

            if (currentPlaceholder === "{campaign_id}" && IsNumber(name)) {
                startSpinner();
                integrationService.updateMgidCampaignStatus({
                    status: status, integrationId: integration?.id, campaignId: name
                }).then((response) => {
                    const updatedRecords = result?.records?.map((record) => {
                        if (record?.name === name) {
                            return {
                                ...record,
                                status: status,
                                deliveryStatus: response?.deliveryStatus,
                            };
                        }
                        return record;
                    });
                    const updatedResult = {...result, records: updatedRecords};
                    updateData(updatedResult);
                }).catch((e) => {
                    stopSpinner(e);
                });
            }

            if (currentPlaceholder === "{teaser_id}" && IsNumber(name)) {
                startSpinner();
                integrationService.updateMgidAdStatus({
                    status: status, integrationId: integration?.id, adId: name
                }).then(() => {
                    const updatedRecords = result?.records?.map((record) => {
                        if (record?.name === name) {
                            return {
                                ...record,
                                status: status,
                                deliveryStatus: status ? "Active" : "Paused",
                            };
                        }
                        return record;
                    });
                    const updatedResult = {...result, records: updatedRecords};
                    updateData(updatedResult);
                }).catch((e) => {
                    stopSpinner(e);
                });
            }

            if (currentPlaceholder === "{widget_id}" && IsNumber(campaignId)) {
                startSpinner();
                integrationService.updateMgidWidgetStatus({
                    status: status, integrationId: integration?.id, campaignId: campaignId, widgetId: name
                }).then(() => {
                    const updatedRecords = result?.records?.map((record) => {
                        if (record?.name === name) {
                            return {...record, status: status};
                        }
                        return record;
                    });
                    const updatedResult = {...result, records: updatedRecords};
                    updateData(updatedResult);
                }).catch((e) => {
                    stopSpinner(e);
                });
            }
        }

        if (integration?.networkType === "outbrain") {

            const outbrainRegex = /^[a-z0-9]{34}$/

            let campaignId = null;
            let campaignValue = GetSelectedPlaceholderNameValue({
                getState, groupBy1Placeholder, groupBy2Placeholder,
                treeViewData: result,
                detail1ParentUUID: topParentUUID,
                detail2ParentUUID: parentRowUUID,
                isTreeView: isTreeView,
                placeholder: "{{campaign_id}}",
            });

            if (campaignValue?.match(outbrainRegex) !== null) {
                campaignId = campaignValue;
            }

            if (currentPlaceholder === "{{campaign_id}}") {
                startSpinner();
                integrationService.updateOutbrainCampaignStatus({
                    status: status, integrationId: integration?.id, campaignId: name
                }).then(() => {
                    const updatedRecords = result?.records?.map((record) => {
                        if (record?.name === name) {
                            return {...record, status: status};
                        }
                        return record;
                    });
                    const updatedResult = {...result, records: updatedRecords};
                    updateData(updatedResult);
                }).catch((e) => {
                    stopSpinner(e);
                });
            }

            if (currentPlaceholder === "{{ad_id}}" && campaignId !== null) {
                startSpinner();
                integrationService.updateOutbrainAdStatus({
                    status: status, integrationId: integration?.id, adId: name
                }).then(() => {
                    const updatedRecords = result?.records?.map((record) => {
                        if (record?.name === name) {
                            return {
                                ...record,
                                status: status,
                                deliveryStatus: status ? "Active" : "Paused",
                            };
                        }
                        return record;
                    });
                    const updatedResult = {...result, records: updatedRecords};
                    updateData(updatedResult);
                }).catch((e) => {
                    stopSpinner(e);
                });
            }

            if (currentPlaceholder === "{{publisher_id}}" && campaignId !== null) {
                startSpinner();
                integrationService.updateOutbrainPublisherStatus({
                    status: status, integrationId: integration?.id, publisherId: name, campaignId: campaignId
                }).then(() => {
                    const updatedRecords = result?.records?.map((record) => {
                        if (record?.name === name) {
                            return {
                                ...record,
                                status: status,
                                deliveryStatus: status ? "Active" : "Paused",
                            };
                        }
                        return record;
                    });
                    const updatedResult = {...result, records: updatedRecords};
                    updateData(updatedResult);
                }).catch((e) => {
                    stopSpinner(e);
                });
            }

            if (currentPlaceholder === "{{section_id}}" && campaignId !== null) {
                startSpinner();
                integrationService.updateOutbrainSectionStatus({
                    status: status, integrationId: integration?.id, sectionId: name, campaignId: campaignId
                }).then(() => {
                    const updatedRecords = result?.records?.map((record) => {
                        if (record?.name === name) {
                            return {
                                ...record,
                                status: status,
                                deliveryStatus: status ? "Active" : "Paused",
                            };
                        }
                        return record;
                    });
                    const updatedResult = {...result, records: updatedRecords};
                    updateData(updatedResult);
                }).catch((e) => {
                    stopSpinner(e);
                });
            }
        }

        if (integration?.networkType === "tiktok") {
            let fbPlaceholders = ["__CAMPAIGN_ID__", "__AID__", "__CID__"]

            let level = "campaign";
            if (currentPlaceholder === "__AID__") {
                level = "adgroup";
            } else if (currentPlaceholder === "__CID__") {
                level = "ad"
            }

            if (fbPlaceholders.indexOf(currentPlaceholder) !== -1) {
                startSpinner()
                integrationService.updateTikTokStatus({
                    status: status, level: level, integrationId: integration?.id, id: name
                }).then((response) => {
                    const updatedRecords = result?.records?.map((record) => {
                        if (record?.name === name) {
                            const isActive = response?.success === true && status;
                            return {
                                ...record,
                                status: status,
                                deliveryStatus: isActive ? "Active" : "Inactive",
                            };
                        }
                        return record;
                    });
                    const updatedResult = {...result, records: updatedRecords};
                    updateData(updatedResult);
                }).catch((e) => {
                    stopSpinner(e);
                });
            }
        }

        if (integration?.networkType === "revcontent") {

            const revContentRegex = /^\d+$/

            let campaignId = null;
            let campaignValue = GetSelectedPlaceholderNameValue({
                getState, groupBy1Placeholder, groupBy2Placeholder,
                treeViewData: result,
                detail1ParentUUID: topParentUUID,
                detail2ParentUUID: parentRowUUID,
                isTreeView: isTreeView,
                placeholder: "{boost_id}",
            });

            if (campaignValue?.match(revContentRegex) !== null) {
                campaignId = campaignValue;
            }

            if (currentPlaceholder === "{boost_id}") {
                startSpinner();
                integrationService.updateRevContentCampaignStatus({
                    status: status, integrationId: integration?.id, campaignId: name
                }).then(() => {
                    const updatedRecords = result?.records?.map((record) => {
                        if (record?.name === name) {
                            return {
                                ...record,
                                status: status,
                                deliveryStatus: status ? "Active" : "Disabled",
                            };
                        }
                        return record;
                    });
                    const updatedResult = {...result, records: updatedRecords};
                    updateData(updatedResult);
                }).catch((e) => {
                    stopSpinner(e);
                });
            }

            if (currentPlaceholder === "{content_id}" && campaignId !== null) {
                startSpinner();
                integrationService.updateRevContentCampaignContentStatus({
                    status: status, integrationId: integration?.id, contentId: name, campaignId: campaignId
                }).then(() => {
                    const updatedRecords = result?.records?.map((record) => {
                        if (record?.name === name) {
                            return {
                                ...record,
                                status: status,
                                deliveryStatus: status ? "Active" : "Paused",
                            };
                        }
                        return record;
                    });
                    const updatedResult = {...result, records: updatedRecords};
                    updateData(updatedResult);
                }).catch((e) => {
                    stopSpinner(e);
                });
            }

            if (currentPlaceholder === "{widget_id}" && campaignId !== null) {
                startSpinner();
                integrationService.updateRevContentCampaignWidgetStatus({
                    status: status, integrationId: integration?.id, widgetId: name, campaignId: campaignId
                }).then(() => {
                    const updatedRecords = result?.records?.map((record) => {
                        if (record?.name === name) {
                            return {...record, status: status};
                        }
                        return record;
                    });
                    const updatedResult = {...result, records: updatedRecords};
                    updateData(updatedResult);
                }).catch((e) => {
                    stopSpinner(e);
                });
            }
        }

        if (integration?.networkType === "outbrain_dsp") {

            if (currentPlaceholder === "{adgroupid}") {
                startSpinner();
                integrationService.updateOutbrainDSPAdGroupStatus({
                    status: status, integrationId: integration?.id, adGroupId: name
                }).then(() => {
                    const updatedRecords = result?.records?.map((record) => {
                        if (record?.name === name) {
                            return {
                                ...record,
                                status: status,
                                deliveryStatus: status ? "Active" : "Paused",
                            };
                        }
                        return record;
                    });
                    const updatedResult = {...result, records: updatedRecords};
                    updateData(updatedResult);
                }).catch((e) => {
                    stopSpinner(e);
                });
            }

            if (currentPlaceholder === "{contentadid}") {
                startSpinner();
                integrationService.updateOutbrainDSPAdStatus({
                    status: status, integrationId: integration?.id, adId: name
                }).then(() => {
                    const updatedRecords = result?.records?.map((record) => {
                        if (record?.name === name) {
                            return {
                                ...record,
                                status: status,
                                deliveryStatus: status ? "Active" : "Paused",
                            };
                        }
                        return record;
                    });
                    const updatedResult = {...result, records: updatedRecords};
                    updateData(updatedResult);
                }).catch((e) => {
                    stopSpinner(e);
                });
            }

            if (currentPlaceholder === "{publisher}" && groupBy1Placeholder === "{adgroupid}") {

                const outbrainDspRegex = /^\d+$/

                let adGroupId = null;
                let adGroupValue = GetSelectedPlaceholderNameValue({
                    getState, groupBy1Placeholder, groupBy2Placeholder,
                    treeViewData: result,
                    detail1ParentUUID: topParentUUID,
                    detail2ParentUUID: parentRowUUID,
                    isTreeView: isTreeView,
                    placeholder: "{adgroupid}",
                });

                if (adGroupValue?.match(outbrainDspRegex) !== null) {
                    adGroupId = adGroupValue;
                }

                startSpinner();

                integrationService.updateOutbrainDSPAdGroupPublisherBlacklist({
                    status: status, integrationId: integration?.id, adGroupId: adGroupId, publisher: name
                }).then(() => {
                    const updatedRecords = result?.records?.map((record) => {
                        if (record?.name === name) {
                            return {
                                ...record,
                                status: status,
                                deliveryStatus: status ? "Active" : "Paused",
                            };
                        }
                        return record;
                    });
                    const updatedResult = {...result, records: updatedRecords};
                    updateData(updatedResult);
                }).catch((e) => {
                    stopSpinner(e);
                });
            }
        }

        if (integration?.networkType === "newsbreak") {
            if (currentPlaceholder === "__CAMPAIGN_ID__") {
                startSpinner();
                integrationService.updateNewsBreakCampaignStatus({
                    status: status, integrationId: integration?.id, campaignId: name
                }).then(() => {
                    const updatedRecords = result?.records?.map((record) => {
                        if (record?.name === name) {
                            return {
                                ...record,
                                status: status,
                                deliveryStatus: status ? "Active" : "Inactive",
                            };
                        }
                        return record;
                    });
                    const updatedResult = {...result, records: updatedRecords};
                    updateData(updatedResult);
                }).catch((e) => {
                    stopSpinner(e);
                });
            }

            if (currentPlaceholder === "__FLIGHT_ID__") {
                startSpinner();
                integrationService.updateNewsBreakAdSetStatus({
                    status: status, integrationId: integration?.id, adSetId: name
                }).then(() => {
                    const updatedRecords = result?.records?.map((record) => {
                        if (record?.name === name) {
                            return {
                                ...record,
                                status: status,
                                deliveryStatus: status ? "Active" : "Inactive",
                            };
                        }
                        return record;
                    });
                    const updatedResult = {...result, records: updatedRecords};
                    updateData(updatedResult);
                }).catch((e) => {
                    stopSpinner(e);
                });
            }

            if (currentPlaceholder === "__CREATIVE_ID__") {
                startSpinner();
                integrationService.updateNewsBreakAdStatus({
                    status: status, integrationId: integration?.id, adId: name
                }).then(() => {
                    const updatedRecords = result?.records?.map((record) => {
                        if (record?.name === name) {
                            return {
                                ...record,
                                status: status,
                                deliveryStatus: status ? "Active" : "Inactive",
                            };
                        }
                        return record;
                    });
                    const updatedResult = {...result, records: updatedRecords};
                    updateData(updatedResult);
                }).catch((e) => {
                    stopSpinner(e);
                });
            }
        }

        if (integration?.networkType === "smartnews") {
            let snPlaceholders = ["{campaign_id}", "{ad_group_id}", "{ad_id}"]

            let level = "campaigns";
            if (currentPlaceholder === "{ad_group_id}") {
                level = "ad_groups";
            } else if (currentPlaceholder === "{ad_id}") {
                level = "ads"
            }

            if (snPlaceholders.indexOf(currentPlaceholder) !== -1) {
                startSpinner();
                integrationService.updateSmartNewsStatus({
                    status: status, level: level, integrationId: integration?.id, id: name
                }).then(() => {
                    const updatedRecords = result?.records?.map((record) => {
                        if (record?.name === name) {
                            return {
                                ...record,
                                status: status,
                                deliveryStatus: status ? "Active" : "Paused",
                            };
                        }
                        return record;
                    });
                    const updatedResult = {...result, records: updatedRecords};
                    updateData(updatedResult);
                }).catch((e) => {
                    stopSpinner(e);
                });
            }
        }

        if (integration?.networkType === "mediago") {
            // eslint-disable-next-line no-template-curly-in-string
            let snPlaceholders = ["${CAMPAIGN_ID}", "${AD_ID}", "${SITE_ID}"]

            let level = "campaign";
            // eslint-disable-next-line no-template-curly-in-string
            if (currentPlaceholder === "${AD_ID}") {
                level = "ad";
                // eslint-disable-next-line no-template-curly-in-string
            } else if (currentPlaceholder === "${SITE_ID}") {
                level = "site"
            }

            // find domain name
            let domainName = ""
            if (level === "site") {
                for (let i of result?.records) {
                    if (i?.name === name) {
                        domainName = i?.trafficSourceName; // site name is mapped under this value.
                        break;
                    }
                }
            }

            // find campaign id
            let mediagoCampaignId = name;

            // eslint-disable-next-line no-template-curly-in-string
            if (currentPlaceholder !== "${CAMPAIGN_ID}") {
                mediagoCampaignId = GetSelectedPlaceholderNameValue({
                    getState, groupBy1Placeholder, groupBy2Placeholder,
                    treeViewData: result,
                    detail1ParentUUID: topParentUUID,
                    detail2ParentUUID: parentRowUUID,
                    isTreeView: isTreeView,
                    // eslint-disable-next-line no-template-curly-in-string
                    placeholder: "${CAMPAIGN_ID}",
                });
            }

            if (snPlaceholders.indexOf(currentPlaceholder) !== -1) {
                startSpinner();
                integrationService.updateMediaGoStatus({
                    status: status,
                    level: level,
                    integrationId: integration?.id,
                    id: name,
                    campaignId: mediagoCampaignId,
                    domainName: domainName
                }).then(() => {
                    const updatedRecords = result?.records?.map((record) => {
                        if (record?.name === name) {
                            return {
                                ...record,
                                status: status,
                                deliveryStatus: status ? "Active" : "Paused",
                            };
                        }
                        return record;
                    });
                    const updatedResult = {...result, records: updatedRecords};
                    updateData(updatedResult);
                }).catch((e) => {
                    stopSpinner(e);
                });
            }
        }

        if (integration?.networkType === "popin") {
            // eslint-disable-next-line no-template-curly-in-string
            let snPlaceholders = ["${CAMPAIGN_ID}", "${SITE_ID}"]

            let level = "campaign";
            // eslint-disable-next-line no-template-curly-in-string
            if (currentPlaceholder === "${SITE_ID}") {
                level = "site"
            }

            // find domain name
            let domainName = ""
            if (level === "site") {
                for (let i of result?.records) {
                    if (i?.name === name) {
                        domainName = i?.trafficSourceName; // site name is mapped under this value.
                        break;
                    }
                }
            }

            // find campaign id
            let popinCampaignId = name;

            // eslint-disable-next-line no-template-curly-in-string
            if (currentPlaceholder !== "${CAMPAIGN_ID}") {
                popinCampaignId = GetSelectedPlaceholderNameValue({
                    getState, groupBy1Placeholder, groupBy2Placeholder,
                    treeViewData: result,
                    detail1ParentUUID: topParentUUID,
                    detail2ParentUUID: parentRowUUID,
                    isTreeView: isTreeView,
                    // eslint-disable-next-line no-template-curly-in-string
                    placeholder: "${CAMPAIGN_ID}",
                });
            }

            if (snPlaceholders.indexOf(currentPlaceholder) !== -1) {
                startSpinner();
                integrationService.updatePopinStatus({
                    status: status,
                    level: level,
                    integrationId: integration?.id,
                    id: name,
                    campaignId: popinCampaignId,
                    domainName: domainName
                }).then(() => {
                    const updatedRecords = result?.records?.map((record) => {
                        if (record?.name === name) {
                            return {
                                ...record,
                                status: status,
                                deliveryStatus: status ? "Active" : "Paused",
                            };
                        }
                        return record;
                    });
                    const updatedResult = {...result, records: updatedRecords};
                    updateData(updatedResult);
                }).catch((e) => {
                    stopSpinner(e);
                });
            }
        }
    };
}

function updateBid({bid, name, dataLevel, isTreeView = false, parentRowUUID = ""}) {
    return (dispatch, getState) => {

        let result = getState().groupBy3.data;
        // Override data source if it is tree view reporting.
        if (isTreeView) {
            result = getState().groupBy1.data;
        }

        let integration = getState().groupBy1.integration;
        let trafficSource = getState().groupBy1.trafficSource;
        let groupBy1 = getState().groupBy1.groupBy?.groupBy;
        let groupBy2 = getState().groupBy2.groupBy?.groupBy;
        let groupBy3 = getState().groupBy3.groupBy?.groupBy;
        let groupBy1Placeholder = GetSelectedGroupByPlaceholderFromTrafficSource(groupBy1, trafficSource);
        let groupBy2Placeholder = GetSelectedGroupByPlaceholderFromTrafficSource(groupBy2, trafficSource);

        // Get current group by placeholder
        let currentPlaceholder = GetSelectedGroupByPlaceholderFromTrafficSource(groupBy3, trafficSource);
        if (dataLevel === LEVEL_1) {
            currentPlaceholder = groupBy1Placeholder;
        } else if (dataLevel === LEVEL_2) {
            currentPlaceholder = groupBy2Placeholder;
        }

        const topParentUUID = GetTopParentUUID(result?.records, parentRowUUID);

        const startSpinner = () => {
            dispatch({
                type: isTreeView
                    ? groupBy1Constants.GROUP_BY_1_LOADING_SPINNER
                    : groupBy3Constants.GROUP_BY_3_LOADING_SPINNER,
            });
            dispatch(alertActions.error(""));
        }

        const stopSpinner = (e) => {
            dispatch(alertActions.error(e?.response?.data?.error));
            dispatch({
                type: isTreeView
                    ? groupBy1Constants.GROUP_BY_1_STOP_LOADING_SPINNER
                    : groupBy3Constants.GROUP_BY_3_STOP_LOADING_SPINNER,
            });
        }

        const updateData = (updatedResult) => {
            dispatch({
                type: isTreeView
                    ? groupBy1Constants.GROUP_BY_1_GET_DATA
                    : groupBy3Constants.GROUP_BY_3_GET_DATA,
                result: updatedResult,
            });
            dispatch(alertActions.success("Updated successfully"));
        }

        if (integration?.networkType === "pushground") {

            if (currentPlaceholder === "{campaign_id}" && IsNumber(name)) {
                startSpinner();
                integrationService.updatePushgroundCampaignBid(bid, integration?.id, name).then(() => {
                    const updatedRecords = result?.records?.map((record) => {
                        if (record?.name === name) {
                            return {...record, bid: bid};
                        }
                        return record;
                    });
                    const updatedResult = {...result, records: updatedRecords};
                    updateData(updatedResult);
                }).catch((e) => {
                    stopSpinner(e);
                });
            }

            if (currentPlaceholder === "{supply_id}") {

                let pushgroundCampaignId = GetSelectedPlaceholderNameValue({
                    getState, groupBy1Placeholder, groupBy2Placeholder,
                    treeViewData: result,
                    detail1ParentUUID: topParentUUID,
                    detail2ParentUUID: parentRowUUID,
                    isTreeView: isTreeView,
                    placeholder: "{campaign_id}",
                });

                if (!IsNumber(pushgroundCampaignId)) {
                    return;
                }

                startSpinner();
                integrationService.updatePushgroundCampaignSupplyBid({
                    bid, integrationId: integration?.id, supplyId: name, pushgroundCampaignId
                }).then(() => {
                    const updatedRecords = result?.records?.map((record) => {
                        if (record?.name === name) {
                            return {...record, bid: bid};
                        }
                        return record;
                    });
                    const updatedResult = {...result, records: updatedRecords};
                    updateData(updatedResult);
                }).catch((e) => {
                    stopSpinner(e);
                });
            }
        }

        if (integration?.networkType === "propeller-ads") {

            if (currentPlaceholder === "{zoneid}") {

                let propellerAdsCampaignId = GetSelectedPlaceholderNameValue({
                    getState, groupBy1Placeholder, groupBy2Placeholder,
                    treeViewData: result,
                    detail1ParentUUID: topParentUUID,
                    detail2ParentUUID: parentRowUUID,
                    isTreeView: isTreeView,
                    placeholder: "{campaignid}",
                });

                startSpinner();
                integrationService.updatePropellerAdsZoneBid({
                    bid, integrationId: integration?.id, campaignId: propellerAdsCampaignId, zoneId: name
                }).then((response) => {
                    const updatedRecords = result?.records?.map((record) => {
                        if (record?.name === name) {
                            return {...record, bid: response?.bid};
                        }
                        return record;
                    });
                    const updatedResult = {...result, records: updatedRecords};
                    updateData(updatedResult);
                }).catch((e) => {
                    stopSpinner(e);
                });
            }

            if (currentPlaceholder === "{campaignid}") {
                startSpinner();
                integrationService.updatePropellerAdsCampaignBid({
                    bid: bid, integrationId: integration?.id, campaignId: name
                }).then((response) => {
                    const updatedRecords = result?.records?.map((record) => {
                        if (record?.name === name) {
                            return {...record, bid: response?.bid};
                        }
                        return record;
                    });
                    const updatedResult = {...result, records: updatedRecords};
                    updateData(updatedResult);
                }).catch((e) => {
                    stopSpinner(e);
                });
            }
        }

        if (integration?.networkType === "exoclick") {

            let campaignId = GetSelectedPlaceholderNameValue({
                getState, groupBy1Placeholder, groupBy2Placeholder,
                treeViewData: result,
                detail1ParentUUID: topParentUUID,
                detail2ParentUUID: parentRowUUID,
                isTreeView: isTreeView,
                placeholder: "{campaign_id}",
            });

            if (currentPlaceholder === "{campaign_id}") {
                startSpinner();
                integrationService.updateExoClickCampaignBid({
                    bid: bid, integrationId: integration?.id, campaignId: name
                }).then((response) => {
                    const updatedRecords = result?.records?.map((record) => {
                        if (record?.name === name) {
                            return {...record, bid: response?.bid};
                        }
                        return record;
                    });
                    const updatedResult = {...result, records: updatedRecords};
                    updateData(updatedResult);
                }).catch((e) => {
                    stopSpinner(e);
                });
            }
            if (currentPlaceholder === "{zone_id}") {
                startSpinner();
                integrationService.updateExoClickZoneBid({
                    bid, integrationId: integration?.id, campaignId: campaignId, zoneId: name
                }).then((response) => {
                    const updatedRecords = result?.records?.map((record) => {
                        if (record?.name === name) {
                            return {...record, bid: response?.bid};
                        }
                        return record;
                    });
                    const updatedResult = {...result, records: updatedRecords};
                    updateData(updatedResult);
                }).catch((e) => {
                    stopSpinner(e);
                });
            }
        }

        if (integration?.networkType === "mondiad") {

            let campaignId = GetSelectedPlaceholderNameValue({
                getState, groupBy1Placeholder, groupBy2Placeholder,
                treeViewData: result,
                detail1ParentUUID: topParentUUID,
                detail2ParentUUID: parentRowUUID,
                isTreeView: isTreeView,
                placeholder: "[campaignid]",
            });

            if (currentPlaceholder === "[campaignid]") {
                startSpinner();
                integrationService.updateMondiadCampaignBid({
                    bid: bid, integrationId: integration?.id, campaignId: name
                }).then((response) => {
                    const updatedRecords = result?.records?.map((record) => {
                        if (record?.name === name) {
                            return {...record, bid: response?.bid};
                        }
                        return record;
                    });
                    const updatedResult = {...result, records: updatedRecords};
                    updateData(updatedResult);
                }).catch((e) => {
                    stopSpinner(e);
                });
            }
            if (currentPlaceholder === "[zoneid]") {
                startSpinner();
                integrationService.updateMondiadZoneBid({
                    bid, integrationId: integration?.id, campaignId: campaignId, zoneId: parseInt(name)
                }).then((response) => {
                    const updatedRecords = result?.records?.map((record) => {
                        if (record?.name === name) {
                            return {...record, bid: response?.bid};
                        }
                        return record;
                    });
                    const updatedResult = {...result, records: updatedRecords};
                    updateData(updatedResult);
                }).catch((e) => {
                    stopSpinner(e);
                });
            }
            if (currentPlaceholder === "[subid]") {
                startSpinner();
                integrationService.updateMondiadSiteBid({
                    bid, integrationId: integration?.id, campaignId: campaignId, siteId: name
                }).then((response) => {
                    const updatedRecords = result?.records?.map((record) => {
                        if (record?.name === name) {
                            return {...record, bid: response?.bid};
                        }
                        return record;
                    });
                    const updatedResult = {...result, records: updatedRecords};
                    updateData(updatedResult);
                }).catch((e) => {
                    stopSpinner(e);
                });
            }
        }

        if (integration?.networkType === "taboola") {

            let campaignId = GetSelectedPlaceholderNameValue({
                getState, groupBy1Placeholder, groupBy2Placeholder,
                treeViewData: result,
                detail1ParentUUID: topParentUUID,
                detail2ParentUUID: parentRowUUID,
                isTreeView: isTreeView,
                placeholder: "{campaign_id}",
            });

            if (currentPlaceholder === "{site}" && IsNumber(campaignId)) {
                startSpinner();
                integrationService.updateTaboolaCampaignSiteBid(
                    {integrationId: integration?.id, campaignId: campaignId, site: name, bid: bid}
                ).then(() => {
                    const updatedRecords = result?.records?.map((record) => {
                        if (record?.name === name) {
                            return {...record, bid: bid};
                        }
                        return record;
                    });
                    const updatedResult = {...result, records: updatedRecords};
                    updateData(updatedResult);
                }).catch((e) => {
                    stopSpinner(e);
                });
            }
        }

        if (integration?.networkType === "mgid") {
            if (currentPlaceholder === "{teaser_id}" && IsNumber(name)) {
                startSpinner();
                integrationService.updateMgidAdBid({
                    bid: bid, integrationId: integration?.id, adId: name
                }).then(() => {
                    const updatedRecords = result?.records?.map((record) => {
                        if (record?.name === name) {
                            return {...record, bid: bid};
                        }
                        return record;
                    });
                    const updatedResult = {...result, records: updatedRecords};
                    updateData(updatedResult);
                }).catch((e) => {
                    stopSpinner(e);
                });
            }
        }

        if (integration?.networkType === "outbrain") {

            const outbrainRegex = /^[a-z0-9]{34}$/

            let campaignId = null;
            let campaignValue = GetSelectedPlaceholderNameValue({
                getState, groupBy1Placeholder, groupBy2Placeholder,
                treeViewData: result,
                detail1ParentUUID: topParentUUID,
                detail2ParentUUID: parentRowUUID,
                isTreeView: isTreeView,
                placeholder: "{{campaign_id}}",
            });

            if (campaignValue?.match(outbrainRegex) !== null) {
                campaignId = campaignValue;
            }

            if (currentPlaceholder === "{{campaign_id}}") {
                startSpinner();
                integrationService.updateOutbrainCampaignBid({
                    bid: bid, integrationId: integration?.id, campaignId: name
                }).then(() => {
                    const updatedRecords = result?.records?.map((record) => {
                        if (record?.name === name) {
                            return {...record, bid: bid};
                        }
                        return record;
                    });
                    const updatedResult = {...result, records: updatedRecords};
                    updateData(updatedResult);
                }).catch((e) => {
                    stopSpinner(e);
                });
            }

            if (currentPlaceholder === "{{section_id}}" && campaignId !== null) {
                startSpinner();
                integrationService.updateOutbrainSectionBid({
                    bid: bid, integrationId: integration?.id, sectionId: name, campaignId: campaignId
                }).then(() => {
                    const updatedRecords = result?.records?.map((record) => {
                        if (record?.name === name) {
                            return {...record, adjustCpcPercentage: bid};
                        }
                        return record;
                    });
                    const updatedResult = {...result, records: updatedRecords};
                    updateData(updatedResult);
                }).catch((e) => {
                    stopSpinner(e);
                });
            }
        }

        if (integration?.networkType === "facebook") {
            if (bid > 0 && ["{{adset.id}}"].indexOf(currentPlaceholder) !== -1) {
                startSpinner();
                integrationService.updateFacebookAdSetBid({
                    bid: bid, integrationId: integration?.id, id: name
                }).then(() => {
                    const updatedRecords = result?.records?.map((record) => {
                        if (record?.name === name) {
                            return {...record, bid: parseFloat(bid)};
                        }
                        return record;
                    });
                    const updatedResult = {...result, records: updatedRecords};
                    updateData(updatedResult);
                }).catch((e) => {
                    stopSpinner(e);
                });
            }
        }

        if (integration?.networkType === "revcontent") {

            const revContentRegex = /^\d+$/

            let campaignId = null;
            let campaignValue = GetSelectedPlaceholderNameValue({
                getState, groupBy1Placeholder, groupBy2Placeholder,
                treeViewData: result,
                detail1ParentUUID: topParentUUID,
                detail2ParentUUID: parentRowUUID,
                isTreeView: isTreeView,
                placeholder: "{boost_id}",
            });

            if (campaignValue?.match(revContentRegex) !== null) {
                campaignId = campaignValue;
            }

            if (currentPlaceholder === "{boost_id}") {
                startSpinner();
                integrationService.updateRevContentCampaignBid({
                    bid: bid, integrationId: integration?.id, campaignId: name
                }).then(() => {
                    const updatedRecords = result?.records?.map((record) => {
                        if (record?.name === name) {
                            return {...record, bid: bid};
                        }
                        return record;
                    });
                    const updatedResult = {...result, records: updatedRecords};
                    updateData(updatedResult);
                }).catch((e) => {
                    stopSpinner(e);
                });
            }

            if (currentPlaceholder === "{widget_id}" && campaignId !== null) {
                startSpinner();
                integrationService.updateRevContentCampaignWidgetBid({
                    bid: bid, integrationId: integration?.id, widgetId: name, campaignId: campaignId
                }).then(() => {
                    const updatedRecords = result?.records?.map((record) => {
                        if (record?.name === name) {
                            return {...record, bid: bid};
                        }
                        return record;
                    });
                    const updatedResult = {...result, records: updatedRecords};
                    updateData(updatedResult);
                }).catch((e) => {
                    stopSpinner(e);
                });
            }
        }

        if (integration?.networkType === "outbrain_dsp") {
            if (currentPlaceholder === "{adgroupid}") {
                startSpinner();
                integrationService.updateOutbrainDSPAdGroupBid({
                    bid: bid, integrationId: integration?.id, adGroupId: name
                }).then(() => {
                    const updatedRecords = result?.records?.map((record) => {
                        if (record?.name === name) {
                            return {...record, bid: bid};
                        }
                        return record;
                    });
                    const updatedResult = {...result, records: updatedRecords};
                    updateData(updatedResult);
                }).catch((e) => {
                    stopSpinner(e);
                });
            }
        }

        if (integration?.networkType === "newsbreak") {
            if (currentPlaceholder === "__FLIGHT_ID__") {
                startSpinner();
                integrationService.updateNewsBreakAdSetBid({
                    bid: bid, integrationId: integration?.id, adSetId: name
                }).then(() => {
                    const updatedRecords = result?.records?.map((record) => {
                        if (record?.name === name) {
                            return {...record, bid: bid};
                        }
                        return record;
                    });
                    const updatedResult = {...result, records: updatedRecords};
                    updateData(updatedResult);
                }).catch((e) => {
                    stopSpinner(e);
                });
            }
        }

        if (integration?.networkType === "mediago") {

            const mediaGoRegex = /^\d+$/;

            let mediaGoCampaignId = null;
            let campaignValue = GetSelectedPlaceholderNameValue({
                getState, groupBy1Placeholder, groupBy2Placeholder,
                treeViewData: result,
                detail1ParentUUID: topParentUUID,
                detail2ParentUUID: parentRowUUID,
                isTreeView: isTreeView,
                // eslint-disable-next-line no-template-curly-in-string
                placeholder: "${CAMPAIGN_ID}",
            });

            if (campaignValue?.match(mediaGoRegex) !== null) {
                mediaGoCampaignId = campaignValue;
            }

            // eslint-disable-next-line no-template-curly-in-string
            if (currentPlaceholder === "${CAMPAIGN_ID}") {
                startSpinner();
                integrationService.updateMediaGoCampaignBidding({
                    bid: bid,
                    integrationId: integration?.id,
                    campaignId: name,
                    level: "campaign",
                    reason: "bid"
                }).then(() => {
                    const updatedRecords = result?.records?.map((record) => {
                        if (record?.name === name) {
                            return {...record, bid: bid};
                        }
                        return record;
                    });
                    const updatedResult = {...result, records: updatedRecords};
                    updateData(updatedResult);
                }).catch((e) => {
                    stopSpinner(e);
                });
            }

            // eslint-disable-next-line no-template-curly-in-string
            if (currentPlaceholder === "${SITE_ID}" && mediaGoCampaignId !== null) {
                startSpinner();
                integrationService.updateMediaGoCampaignBidding({
                    bid: bid,
                    integrationId: integration?.id,
                    campaignId: mediaGoCampaignId,
                    siteId: name,
                    level: "site",
                    reason: "bid"
                }).then(() => {
                    const updatedRecords = result?.records?.map((record) => {
                        if (record?.name === name) {
                            return {...record, bid: bid};
                        }
                        return record;
                    });
                    const updatedResult = {...result, records: updatedRecords};
                    updateData(updatedResult);
                }).catch((e) => {
                    stopSpinner(e);
                });
            }
        }

        if (integration?.networkType === "popin") {

            const popinRegex = /^\d+$/;
            let campaignId = null;
            let campaignValue = GetSelectedPlaceholderNameValue({
                getState, groupBy1Placeholder, groupBy2Placeholder,
                treeViewData: result,
                detail1ParentUUID: topParentUUID,
                detail2ParentUUID: parentRowUUID,
                isTreeView: isTreeView,
                // eslint-disable-next-line no-template-curly-in-string
                placeholder: "${CAMPAIGN_ID}",
            });

            if (campaignValue?.match(popinRegex) !== null) {
                campaignId = campaignValue;
            }

            // eslint-disable-next-line no-template-curly-in-string
            if (currentPlaceholder === "${CAMPAIGN_ID}") {
                startSpinner();
                integrationService.updatePopinCampaignBidding({
                    bid: bid,
                    integrationId: integration?.id,
                    campaignId: name,
                    level: "campaign",
                    reason: "bid"
                }).then(() => {
                    const updatedRecords = result?.records?.map((record) => {
                        if (record?.name === name) {
                            return {...record, bid: bid};
                        }
                        return record;
                    });
                    const updatedResult = {...result, records: updatedRecords};
                    updateData(updatedResult);
                }).catch((e) => {
                    stopSpinner(e);
                });
            }

            // eslint-disable-next-line no-template-curly-in-string
            if (currentPlaceholder === "${SITE_ID}" && campaignId !== null) {
                startSpinner();
                integrationService.updatePopinCampaignBidding({
                    bid: bid,
                    integrationId: integration?.id,
                    campaignId: campaignId,
                    siteId: name,
                    level: "site",
                    reason: "bid"
                }).then(() => {
                    const updatedRecords = result?.records?.map((record) => {
                        if (record?.name === name) {
                            return {...record, bid: bid};
                        }
                        return record;
                    });
                    const updatedResult = {...result, records: updatedRecords};
                    updateData(updatedResult);
                }).catch((e) => {
                    stopSpinner(e);
                });
            }
        }
    };
}

function updateBudget({amount, name, budgetId, dataLevel, isTreeView = false}) {
    return (dispatch, getState) => {

        let result = getState().groupBy3.data;
        // Override data source if it is tree view reporting.
        if (isTreeView) {
            result = getState().groupBy1.data;
        }

        let integration = getState().groupBy1.integration;
        let trafficSource = getState().groupBy1.trafficSource;
        let groupBy1 = getState().groupBy1.groupBy?.groupBy;
        let groupBy2 = getState().groupBy2.groupBy?.groupBy;
        let groupBy3 = getState().groupBy3.groupBy?.groupBy;
        let groupBy1Placeholder = GetSelectedGroupByPlaceholderFromTrafficSource(groupBy1, trafficSource);
        let groupBy2Placeholder = GetSelectedGroupByPlaceholderFromTrafficSource(groupBy2, trafficSource);

        // Get current group by placeholder
        let currentPlaceholder = GetSelectedGroupByPlaceholderFromTrafficSource(groupBy3, trafficSource);
        if (dataLevel === LEVEL_1) {
            currentPlaceholder = groupBy1Placeholder;
        } else if (dataLevel === LEVEL_2) {
            currentPlaceholder = groupBy2Placeholder;
        }

        const startSpinner = () => {
            dispatch({
                type: isTreeView
                    ? groupBy1Constants.GROUP_BY_1_LOADING_SPINNER
                    : groupBy3Constants.GROUP_BY_3_LOADING_SPINNER,
            });
            dispatch(alertActions.error(""));
        }

        const stopSpinner = (e) => {
            dispatch(alertActions.error(e?.response?.data?.error));
            dispatch({
                type: isTreeView
                    ? groupBy1Constants.GROUP_BY_1_STOP_LOADING_SPINNER
                    : groupBy3Constants.GROUP_BY_3_STOP_LOADING_SPINNER,
            });
        }

        const updateData = (updatedResult) => {
            dispatch({
                type: isTreeView
                    ? groupBy1Constants.GROUP_BY_1_GET_DATA
                    : groupBy3Constants.GROUP_BY_3_GET_DATA,
                result: updatedResult,
            });
            dispatch(alertActions.success("Updated successfully"));
        }

        if (integration?.networkType === "facebook") {
            if (amount > 0 && ["{{campaign.id}}", "{{adset.id}}", "{{ad.id}}"].indexOf(currentPlaceholder) !== -1) {
                startSpinner();
                integrationService.updateFacebookDailyBudget({
                    dailyBudget: amount, integrationId: integration?.id, id: name
                }).then(() => {
                    const updatedRecords = result?.records?.map(record =>
                        record?.name === name
                            ? {
                                ...record,
                                fbDailyBudget: parseFloat(amount)
                            }
                            : record
                    );
                    const updatedResult = {...result, records: updatedRecords};
                    updateData(updatedResult);
                }).catch((e) => {
                    stopSpinner(e);
                });
            }
        }

        if (integration?.networkType === "tiktok") {
            let ttPlaceholders = ["__CAMPAIGN_ID__", "__AID__"]
            let level = "campaign";
            if (currentPlaceholder === "__AID__") {
                level = "adgroup";
            }

            if (amount > 0 && ttPlaceholders.indexOf(currentPlaceholder) !== -1) {
                startSpinner();
                integrationService.updateTikTokDailyBudget({
                    dailyBudget: amount, level: level, integrationId: integration?.id, id: name
                }).then((response) => {
                    if (!response?.success) {
                        dispatch(alertActions.error(response?.message));
                        stopSpinner(response?.message);
                    } else {
                        const updatedRecords = result?.records?.map(record =>
                            record?.name === name
                                ? {
                                    ...record,
                                    fbDailyBudget: amount
                                }
                                : record
                        );
                        const updatedResult = {...result, records: updatedRecords};
                        updateData(updatedResult);
                    }
                }).catch((e) => {
                    stopSpinner(e);
                });
            }
        }

        if (integration?.networkType === "outbrain") {
            if (amount > 0 && currentPlaceholder === "{{campaign_id}}") {
                startSpinner();
                integrationService.updateOutbrainCampaignBudget({
                    amount: amount, integrationId: integration?.id, budgetId: budgetId
                }).then(() => {
                    const updatedRecords = result?.records?.map(record =>
                        record?.name === name
                            ? {
                                ...record,
                                fbDailyBudget: amount
                            }
                            : record
                    );
                    const updatedResult = {...result, records: updatedRecords};
                    updateData(updatedResult);
                }).catch((e) => {
                    stopSpinner(e);
                });
            }
        }

        if (integration?.networkType === "taboola") {
            if (currentPlaceholder === "{campaign_id}" && IsNumber(name)) {
                startSpinner();
                integrationService.updateTaboolaCampaignDailyBudget(
                    {amount: amount, integrationId: integration?.id, campaignId: name}
                ).then(() => {
                    const updatedRecords = result?.records?.map(record =>
                        record?.name === name
                            ? {
                                ...record,
                                fbDailyBudget: amount
                            }
                            : record
                    );
                    const updatedResult = {...result, records: updatedRecords};
                    updateData(updatedResult);
                }).catch((e) => {
                    stopSpinner(e);
                });
            }
        }

        if (integration?.networkType === "revcontent") {
            if (amount > 0 && currentPlaceholder === "{boost_id}") {
                startSpinner();
                integrationService.updateRevContentCampaignBudget({
                    budget: amount, integrationId: integration?.id, campaignId: name
                }).then(() => {
                    const updatedRecords = result?.records?.map(record =>
                        record?.name === name
                            ? {
                                ...record,
                                fbDailyBudget: amount
                            }
                            : record
                    );
                    const updatedResult = {...result, records: updatedRecords};
                    updateData(updatedResult);
                }).catch((e) => {
                    stopSpinner(e);
                });
            }
        }

        if (integration?.networkType === "outbrain_dsp") {
            if (amount > 0 && currentPlaceholder === "{adgroupid}") {
                startSpinner();
                integrationService.updateOutbrainDSPAdGroupDailyBudget({
                    amount: amount, integrationId: integration?.id, adGroupId: name
                }).then(() => {
                    const updatedRecords = result?.records?.map(record =>
                        record?.name === name
                            ? {
                                ...record,
                                fbDailyBudget: amount
                            }
                            : record
                    );
                    const updatedResult = {...result, records: updatedRecords};
                    updateData(updatedResult);
                }).catch((e) => {
                    stopSpinner(e);
                });
            }
        }

        if (integration?.networkType === "newsbreak") {
            if (amount > 0 && currentPlaceholder === "__FLIGHT_ID__") {
                startSpinner();
                integrationService.updateNewsBreakAdSetDailyBudget({
                    amount: amount, integrationId: integration?.id, adSetId: name
                }).then(() => {
                    const updatedRecords = result?.records?.map(record =>
                        record?.name === name
                            ? {
                                ...record,
                                fbDailyBudget: amount
                            }
                            : record
                    );
                    const updatedResult = {...result, records: updatedRecords};
                    updateData(updatedResult);
                }).catch((e) => {
                    stopSpinner(e);
                });
            }
        }

        if (integration?.networkType === "smartnews") {
            let snPlaceholders = ["{campaign_id}"]

            if (amount > 0 && snPlaceholders.indexOf(currentPlaceholder) !== -1) {
                startSpinner();
                integrationService.updateSmartNewsDailyBudget({
                    dailyBudget: amount, integrationId: integration?.id, id: name
                }).then(() => {
                    const updatedRecords = result?.records?.map(record =>
                        record?.name === name
                            ? {
                                ...record,
                                fbDailyBudget: amount
                            }
                            : record
                    );
                    const updatedResult = {...result, records: updatedRecords};
                    updateData(updatedResult);
                }).catch((e) => {
                    stopSpinner(e);
                });
            }
        }

        if (integration?.networkType === "mediago") {
            // eslint-disable-next-line no-template-curly-in-string
            if (amount > 0 && currentPlaceholder === "${CAMPAIGN_ID}") {
                startSpinner();
                integrationService.updateMediaGoCampaignBidding({
                    dailyBudget: amount,
                    integrationId: integration?.id,
                    campaignId: name,
                    level: "campaign",
                    reason: "dailyBudget"
                }).then(() => {
                    const updatedRecords = result?.records?.map(record =>
                        record?.name === name
                            ? {
                                ...record,
                                fbDailyBudget: amount
                            }
                            : record
                    );
                    const updatedResult = {...result, records: updatedRecords};
                    updateData(updatedResult);
                }).catch((e) => {
                    stopSpinner(e);
                });
            }
        }

        if (integration?.networkType === "popin") {
            // eslint-disable-next-line no-template-curly-in-string
            if (amount > 0 && currentPlaceholder === "${CAMPAIGN_ID}") {
                startSpinner();
                integrationService.updatePopinCampaignBidding({
                    dailyBudget: amount,
                    integrationId: integration?.id,
                    campaignId: name,
                    level: "campaign",
                    reason: "dailyBudget"
                }).then(() => {
                    const updatedRecords = result?.records?.map(record =>
                        record?.name === name
                            ? {
                                ...record,
                                fbDailyBudget: amount
                            }
                            : record
                    );
                    const updatedResult = {...result, records: updatedRecords};
                    updateData(updatedResult);
                }).catch((e) => {
                    stopSpinner(e);
                });
            }
        }
    };
}

function reset() {
    return dispatch => {
        dispatch({type: groupBy3Constants.GROUP_BY_3_RESET});
    };
}